import {
  builderItems,
  crewsWithBoth,
  productsForRoofType,
  estimateDeleteBlocker,
  liveDocuments,
  jobIsWon,
  computeLadder,
  computeAdditions,
  activeAutoExpenses,
  deckOverPrice,
  estimateLineDescription,
  planDeckOver,
  DECK_OVER_PRODUCTS,
  DECK_OVER_SUPERSEDES,
  changeOrderDeleteConfirmation,
  changeOrderState,
  unsignedChangeOrderWarning,
  TARGET_MARGIN,
  creditMemoFromRead,
  parseCreditMemo,
  returnCredit,
  returnCreditIsTyped,
  DEFAULT_MATERIAL_TAX_RATE,
  documentSubtotal,
  documentTax,
  documentTotal,
  formatCents,
  formatPercent,
  multiOptionHint,
  PAYMENT_TYPES,
  PAYMENT_TYPE_LABEL,
  rateFromTotal,
  searchCatalogue,
  toCents,
  toDecimal,
  UNITS,
  type Additions,
  type Cents,
  type ChangeOrder,
  type CreditMemo,
  type GeneratorProduct,
  type Estimate,
  type EstimateItem,
  type Job,
  type JobExpense,
  type JobFileEntry,
  type LadderTotals,
  type MaterialReturn,
  type Product,
  type Row as TrackedRow,
  type Supplier,
  estimateOptionLabel,
  estimateOptionLabels,
  type PackageCopyLine,
  type Crew,
  type LadderInput,
  type RepricePlan,
  crewLabel,
  mountainBrookFeeApplies,
  signatureFor,
} from "@roof-hawk/core";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ErrorNote, GrowBox, Modal, Row, Stub } from "../../components/ui";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";
import { CraneOption } from "./CraneOption";
import {
  AddExpenseLine,
  InsuranceRowControls,
  insuranceCrewChoices,
} from "./InsuranceLines";
import { MarginModal } from "./MarginModal";
import { EstimateLineModal } from "./EstimateLineModal";
import { PaymentSchedule } from "./PaymentSchedule";
import { InvoiceModal } from "./InvoiceModal";
import { PresentChangeOrderModal } from "./PresentChangeOrderModal";
import { PresentEstimateModal } from "./PresentEstimateModal";
import {
  DeleteLineButton,
  EditHistoryProvider,
  UndoRedoButtons,
  useTrackedRows,
} from "../../lib/editHistory";
import { ExpenseModal } from "./ExpenseModal";
import { ExpenseReceiptButtons, useExpenseReceipts } from "./ExpenseReceipts";
import { t, tn } from "../../i18n";
import { fmtDate, fmtDateTime } from "../../lib/format";
import { placeDropdown, visibleArea } from "../../lib/placeDropdown";
import { fetchFramingNails } from "../../lib/framingNails";
import { useAsync } from "../../lib/hooks";
import {
  deleteRow,
  fetchChangeOrderDocuments,
  fetchCompanySettings,
  fetchEstimateChildren,
  fetchEstimateDocumentsOffered,
  fetchAutoExpenseAdjustments,
  fetchLadderInputs,
  fetchRoofLayouts,
  fetchEstimates,
  fetchJobMultiOption,
  fetchReferenceData,
  insertRow,
  signedContracts,
  updateRow,
  voidChangeOrderDocument,
  voidProposal,
  type ChangeOrderDocumentRecord,
  fetchEstimateLineNames,
  type EstimateInsuranceLine,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import {
  CopyFromEstimateModal,
  type CopyBucket,
} from "../orders/CopyFromEstimateModal";
import { CompanyCamPhotoPicker } from "../orders/CompanyCamPhotoPicker";
import { GenerateOrderModal } from "../orders/GenerateOrderModal";
import { MaterialOrderEditor } from "../orders/MaterialOrderEditor";
import { WorkOrderEditor } from "../orders/WorkOrderEditor";
import { CreateEstimateModal } from "./CreateEstimateModal";
import { JobTotals, type JobTotalsOption } from "./JobTotals";
import { MirrorSection, useMirror } from "./useMirror";

const NUMERIC_ESTIMATE_FIELDS = new Set([
  "office_discount",
  "job_overhead_pct",
]);

/**
 * A `numeric` column as PostgREST hands it over, read back as a number.
 *
 * PostgREST sends numeric as a string so nothing is lost on the way, and
 * `Number("")` is 0 — which would turn an unanswered rate into 0% APR and
 * print an interest-free loan on a customer's copy. Null stays null.
 */
const numberOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The estimate card: builder, change orders, work orders, material orders and
 * other expenses, in that fixed order.
 *
 * Every number displayed comes from `computeLadder` in @roof-hawk/core, which
 * is covered by the golden tests. Nothing in this file does money arithmetic.
 */
export function EstimateCard({
  job,
  onPatchJob,
  onEstimateCreated,
  onPresented,
  onOpenMeasurements,
  workOrderFocus,
  onWorkOrderFocused,
}: {
  job: Job;
  /**
   * Saves a change to the job and updates the copy this card was handed.
   * Without it the multi-option checkbox writes to the database and goes on
   * rendering the old value, which reads as a control that does not work.
   */
  onPatchJob?: (patch: Partial<Job>) => Promise<void>;
  /** Ticks the Estimate step once the job has one. */
  onEstimateCreated?: () => Promise<void>;
  /**
   * A document has been handed to the customer.
   *
   * The database ticks "Estimate Sent" (20260919100000), which is what moves
   * the job to Proposal Sent — but the checklist and the stage live a screen
   * up, and neither knows a proposal was just created. Without this the rep
   * presses Copy link, goes back to the checklist, and finds the step still
   * pending until they reload the app.
   */
  onPresented?: () => Promise<void>;
  /**
   * Leaves for the Roof Measurements card.
   *
   * Generating from measurements needs a measured roof, and the rep who picked
   * that path on a job without one was told so only after choosing a package
   * and pressing Submit — and then left to find the step themselves. The mirror
   * of `onOpenEstimate`, which the checklist has always had.
   */
  onOpenMeasurements?: () => void;
  /**
   * A work order to open, or "list" for the Work Orders section, when the
   * checklist sent somebody here.
   *
   * Jonathon, 2026-09-08: clicking All Work Complete "took me to the estimate
   * page but it wouldn't actually mark the work orders complete." The step
   * only changed tab, and the box that moves is "Mark As Complete" inside a
   * work order — several screens down a long page, behind an Edit button. So
   * the card finishes the journey the step started.
   */
  workOrderFocus?: string | null;
  /** Said once the focus has been acted on, so it does not fire again. */
  onWorkOrderFocused?: () => void;
}) {
  const { profileId, trades } = useSession();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [offered, setOffered] = useState(false);
  const [editingLine, setEditingLine] = useState<EstimateItem | null>(null);
  const [showAdditions, setShowAdditions] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState<string | null>(null);
  const [editingMaterialOrder, setEditingMaterialOrder] = useState<
    string | null
  >(null);
  const [presenting, setPresenting] = useState(false);
  const [presentingChangeOrder, setPresentingChangeOrder] = useState(false);
  /*
   * The two ways to fill an order that is not typed out by hand.
   *
   * `generating` builds one off the roof measurements from a template, without
   * the template's wording; `copying` takes one off another estimate on this
   * job. Both are per-bucket, so one piece of state each rather than six.
   */
  const [generating, setGenerating] = useState<"material" | "work" | null>(
    null,
  );
  const [copying, setCopying] = useState<CopyBucket | null>(null);
  const [invoicing, setInvoicing] = useState(false);
  /** Which document is being withdrawn, so its button says so. */
  const [voiding, setVoiding] = useState<string | null>(null);

  const reference = useAsync(fetchReferenceData, []);
  const companyQuery = useAsync(fetchCompanySettings, []);
  // Read here rather than inside the modal, which takes everything it needs as
  // props so that it can be rendered on its own in a test.
  const {
    account: gmailAccount,
    loading: gmailLoading,
    error: gmailError,
  } = useGmailAccount();
  // Who this person may send an estimate as. Read here rather than inside the
  // modal, which takes everything it needs as props so it can be rendered on
  // its own in a test.
  const {
    senders: gmailSenders,
    own: ownGmailSender,
    error: sendersError,
  } = useGmailSenders();
  const estimatesQuery = useAsync(() => fetchEstimates(job.id), [job.id]);
  /*
   * The multi-option box, read fresh rather than off `job`.
   *
   * The database sets it for itself whenever an estimate on this job is added,
   * deleted or retyped (ADR 0120), and `job` is whatever the job screen loaded
   * before any of that. Reloaded after each of those, so the box and "Set for
   * you" say what is true now. `job` is only the answer until this arrives.
   */
  const flagQuery = useAsync(() => fetchJobMultiOption(job.id), [job.id]);
  const multiOption = flagQuery.data?.multi_option ?? Boolean(job.multi_option);
  const multiOptionManual =
    flagQuery.data?.multi_option_manual ?? Boolean(job.multi_option_manual);
  const estimates = estimatesQuery.data ?? [];

  const estimate =
    estimates.find((e) => e.id === selectedId) ??
    estimates.find((e) => e.is_active) ??
    estimates[estimates.length - 1] ??
    null;

  const childrenQuery = useAsync(
    async () => (estimate ? fetchEstimateChildren(estimate.id) : null),
    [job.id, estimate?.id],
  );

  const children = childrenQuery.data;

  /**
   * Orders on this estimate that have already been paid out.
   *
   * Both kinds, because the database refuses the delete over either. Empty
   * while the children are still loading, which is the safe way round: the
   * button appears once the answer is known rather than flickering away.
   */
  const paidOrders = useMemo(
    () =>
      [...(children?.workOrders ?? []), ...(children?.materialOrders ?? [])]
        .filter((order) => order.status === "paid")
        .map((order) => ({ status: order.status })),
    [children],
  );

  // The automatic expenses need to know what kind of job this is, how big the
  // roof is, and which crew is on each work order.
  const layoutsQuery = useAsync(() => fetchRoofLayouts(job.id), [job.id]);
  // What each option's first line is called, for the tabs. Only this estimate's
  // children are fetched in full, and a tab reading "Estimate 2" says nothing
  // about which option it is — see `estimateOptionLabel`.
  const lineNames = useAsync(
    () => fetchEstimateLineNames(estimates.map((e) => e.id)),
    [estimates.map((e) => e.id).join(",")],
  );
  // Made distinct, so two options that start with the same line are two tabs
  // a rep can tell apart (review of #254). The open estimate's lines are the
  // ones on screen, so an edit renames its tab without waiting for a refetch.
  const optionLabels = estimateOptionLabels(
    estimates,
    children && estimate
      ? new Map<string, readonly PackageCopyLine[]>(lineNames.data ?? []).set(
          estimate.id,
          children.items,
        )
      : (lineNames.data ?? new Map()),
  );
  // Amounts typed over the calculated expenses. Held at the card rather than
  // in the expense section, because the ladder needs them as much as the row
  // does — an edited lift the totals never saw would put the margin at the top
  // of the screen out of step with the expense at the bottom of it.
  const overridesQuery = useAsync(
    async () =>
      estimate
        ? await fetchAutoExpenseAdjustments(estimate.id)
        : { overrides: {}, removals: {} },
    [estimate?.id],
  );

  /**
   * The documents this estimate has been offered on.
   *
   * Only to answer whether it can be cleared away: a packet the customer can
   * still open holds the delete, and voiding it is what releases it. Empty
   * while it loads, which is the safe way round — the delete button appears
   * once the answer is known rather than being taken back.
   */
  const documentsQuery = useAsync(
    async () =>
      estimate ? await fetchEstimateDocumentsOffered(estimate.id) : [],
    [estimate?.id],
  );
  const documents = documentsQuery.data ?? [];

  /**
   * The change orders this estimate has sent out.
   *
   * Only so a line can say where it has got to, and so a live one can be
   * withdrawn. Empty while it loads, which reads as "not sent" for a moment —
   * the safe way round, because the alternative is a Send button that is
   * missing when somebody needs it.
   */
  const changeOrderDocsQuery = useAsync(
    async () => (estimate ? await fetchChangeOrderDocuments(estimate.id) : []),
    [estimate?.id],
  );
  const changeOrderDocs: ChangeOrderDocumentRecord[] =
    changeOrderDocsQuery.data ?? [];

  /**
   * What this estimate was signed for, on a Mountain Brook job only: its
   * permit is worked out on the estimate's own price until it is signed and on
   * what was signed from then on (ADR 0289). Nothing else on the card reads
   * it, so no other job asks.
   */
  const mountainBrook = estimate
    ? mountainBrookFeeApplies(job, estimate.created_at)
    : false;
  const signatureQuery = useAsync(
    async () =>
      estimate && mountainBrook
        ? signatureFor(estimate.id, await signedContracts(job.id))
        : null,
    [job.id, estimate?.id, mountainBrook],
  );
  const signature = signatureQuery.data ?? null;

  const estimateTotals: LadderTotals | null = useMemo(() => {
    if (!estimate || !children) return null;
    return computeLadder({
      estimate,
      items: children.items,
      changeOrders: children.changeOrders,
      materialOrders: children.materialOrders,
      workOrders: children.workOrders,
      expenses: children.expenses,
      returns: children.returns,
      jobType: job.job_type,
      place: job,
      signature,
      layouts: layoutsQuery.data ?? [],
      autoExpenseOverrides: overridesQuery.data?.overrides ?? null,
      autoExpenseRemovals: overridesQuery.data?.removals ?? null,
      workOrderCrews: children.workOrders.map((order) => order.crew ?? null),
      insuranceLines: children.insuranceLines ?? [],
    });
  }, [
    estimate,
    children,
    // The whole job: its city decides the Mountain Brook permit (ADR 0289).
    job,
    signature,
    layoutsQuery.data,
    overridesQuery.data,
  ]);

  /**
   * The same four figures for the whole job, where more than one option sold.
   *
   * The strip above is one estimate, because a ladder is one estimate (ADR
   * 0030). A customer who signs for the roof and the gutters has bought one
   * job, and until this nothing on the screen added the two up — Jonathon,
   * 2026-09-23: "we need to know these items for the entire job and for each
   * estimate they select."
   *
   * Only asked for on a job that has more than one active estimate, which is
   * what `recompute_active_estimates` means by sold: every approved estimate
   * once anything is signed, and the single one being forecast before that. On
   * every ordinary job this fetch never happens and `JobTotals` renders
   * nothing.
   *
   * Read through `fetchLadderInputs`, the same call the contract total and the
   * Sold scoreboard go through, rather than assembled here: a roof is measured
   * once however many ways it is priced, and that function is where the rule
   * that the measured expenses are charged once per job lives. Re-fetched
   * whenever this estimate's children change, which is after every save, so
   * the table follows an edit rather than waiting for a reopen.
   */
  const activeCount = estimates.filter((e) => e.is_active).length;
  const jobInputsQuery = useAsync(async () => {
    if (activeCount < 2) return null;
    const byJob = await fetchLadderInputs([job.id]);
    return byJob.get(job.id) ?? [];
  }, [job.id, activeCount, childrenQuery.data]);

  const jobOptions: JobTotalsOption[] = useMemo(() => {
    const inputs = jobInputsQuery.data;
    if (!inputs) return [];
    return inputs.map((input) => {
      // `fetchLadderInputs` hands back the estimate row it read, so the id is
      // on it; the ladder's own type only names the columns it does money with.
      const id = (input.estimate as unknown as { id: string }).id;
      return {
        estimateId: id,
        label: optionLabels.get(id) ?? t("Estimate"),
        totals: computeLadder({ ...input, jobType: job.job_type, place: job }),
      };
    });
  }, [jobInputsQuery.data, optionLabels, job]);

  // On a job with several active estimates, fetchLadderInputs assigns the
  // shared roof measurement to the first one only. Use that same ladder for
  // the open estimate's strip and detail, so its figures agree with its row in
  // the whole-job table and with the job totals elsewhere in the app.
  const totals =
    jobOptions.find((option) => option.estimateId === estimate?.id)?.totals ??
    estimateTotals;

  /**
   * What arrived after the job went on the calendar.
   *
   * Separate from the ladder on purpose: this is not what the job cost, it is
   * what the estimate missed. A job that has never been scheduled has no
   * answer, which `computeAdditions` reports as null rather than zero.
   */
  const additions: Additions = useMemo(
    () =>
      computeAdditions({
        scheduledAt: job.scheduled_at,
        materialOrders: children?.materialOrders ?? [],
        workOrders: children?.workOrders ?? [],
        expenses: children?.expenses ?? [],
      }),
    [job.scheduled_at, children],
  );

  /**
   * Turn an "OSB Deck Over" line into the work it actually is.
   *
   * Raises a Home Depot material order for the sheathing, nails and clips,
   * adds the labour to the job's work order at a rate per sheet, strips the
   * template's patch-repair allowance off the roofing order, and prices the
   * line at the same margin the roof is sold at.
   *
   * Idempotent by supplier: running it twice replaces the Home Depot order
   * rather than raising a second one, because the obvious thing to do after
   * changing the roof size is to open the line and save it again.
   */
  const applyDeckOver = async (lineId: string) => {
    const roof = layoutsQuery.data?.[0];
    if (!roof || !estimate) return;

    const catalogue = unwrap<GeneratorProduct[]>(
      await supabase
        .from("products")
        .select(
          "id,name,category,kind,unit,default_cost,coverage,waste,measurement",
        )
        .eq("active", true),
    );

    // The nails come in the kind the crew's gun takes (ADR 0287). The crew is
    // the one on this estimate's work order — the labour below goes there too.
    const nails = await fetchFramingNails({
      estimateId: estimate.id,
      trade: estimate.trade,
    });
    const plan = planDeckOver(
      roof,
      nails.tag(catalogue),
      DEFAULT_MATERIAL_TAX_RATE,
      nails.nailer,
    );
    if (plan.missing.length > 0) {
      window.alert(
        t(
          "These products are missing from the catalogue, so the deck over is incomplete:",
        ) +
          "\n\n" +
          plan.missing.map((m) => `• ${m}`).join("\n"),
      );
    }
    if (plan.sheets <= 0) return;

    const homeDepot = (reference.data?.suppliers ?? []).find(
      (s) => s.name === "Home Depot",
    );

    // Replace rather than add: saving the line twice must not order twice.
    const existing = (children?.materialOrders ?? []).filter(
      (o) => o.supplier_id === homeDepot?.id,
    );
    for (const order of existing) {
      await deleteRow("material_orders", order.id);
    }

    const order = await insertRow<{ id: string }>("material_orders", {
      job_id: job.id,
      estimate_id: estimate.id,
      supplier_id: homeDepot?.id ?? null,
      trade: estimate.trade,
      status: "not_placed",
      tax_rate: DEFAULT_MATERIAL_TAX_RATE,
      created_by: profileId,
    });

    let sort = 0;
    for (const line of plan.materialLines) {
      await insertRow("material_order_items", {
        material_order_id: order.id,
        product_id: line.product.id,
        name: [line.product.category, line.product.name]
          .filter(Boolean)
          .join(" | "),
        qty: line.qty,
        unit: line.product.unit,
        unit_price: toDecimal(line.unitPrice),
        calc_note: `Deck over = ${plan.sheets} sheets`,
        sort_order: (sort += 10),
      });
    }

    // Labour goes on the job's existing work order — it is the same crew.
    const workOrder = (children?.workOrders ?? [])[0];
    if (workOrder && plan.laborLine) {
      await insertRow("work_order_items", {
        work_order_id: workOrder.id,
        product_id: plan.laborLine.product.id,
        description: plan.laborLine.product.name,
        qty: plan.laborLine.qty,
        unit: "EA",
        unit_price: toDecimal(plan.laborLine.unitPrice),
        calc_note: `Deck over = ${plan.sheets} sheets`,
        sort_order: 500,
      });
    }

    // The template's three sheets and three 1x6s were the patch allowance.
    for (const materialOrder of children?.materialOrders ?? []) {
      if (materialOrder.supplier_id === homeDepot?.id) continue;
      for (const item of materialOrder.items ?? []) {
        const bare = (item.name ?? "").split("|").pop()?.trim() ?? "";
        if (DECK_OVER_SUPERSEDES.includes(bare)) {
          await deleteRow("material_order_items", item.id);
        }
      }
    }

    const price = deckOverPrice(plan.cost, TARGET_MARGIN);
    await updateRow("estimate_items", lineId, {
      qty: plan.sheets,
      unit: "EA",
      unit_price: rateFromTotal(price, plan.sheets),
      customer_price: toDecimal(price),
    });
  };

  const reload = useCallback(async () => {
    await childrenQuery.reload();
    // Presenting or withdrawing a change order moves every line's status, and
    // the statuses live on a different query from the lines.
    await changeOrderDocsQuery.reload();
  }, [childrenQuery, changeOrderDocsQuery]);

  const guard = useCallback(async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  /**
   * "Do the same to the other estimates?"
   *
   * Three options on one job means the same edit made three times, by hand,
   * with the third one forgotten. This watches each section and offers to
   * repeat what happened in it — see useMirror.tsx, and the rules in
   * packages/core/src/mirror.ts.
   */
  const mirror = useMirror({
    job,
    estimate,
    estimates,
    children,
    onApplied: reload,
  });

  /**
   * Finishing the journey the checklist step started.
   *
   * Waits for the children, because the order to open is one of them and the
   * section to scroll to has not rendered until they arrive. It fires once —
   * `onWorkOrderFocused` clears the request — so coming back to this tab an
   * hour later does not reopen a work order somebody has finished with.
   *
   * A named order that is no longer on this estimate falls back to the
   * section. That happens on a job with two estimates: the checklist reads
   * every work order on the JOB, and this card shows one estimate's.
   */
  const workSection = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!workOrderFocus || !children) return;
    const wanted =
      workOrderFocus !== "list" &&
      children.workOrders.some((order) => order.id === workOrderFocus)
        ? workOrderFocus
        : null;
    if (wanted) {
      mirror.begin("work", wanted);
      setEditingWorkOrder(wanted);
    } else {
      workSection.current?.scrollIntoView({ block: "start" });
    }
    onWorkOrderFocused?.();
  }, [workOrderFocus, children, onWorkOrderFocused, mirror]);

  const [settingMargin, setSettingMargin] = useState(false);

  /**
   * Everything `planReprice` needs, in one place.
   *
   * Assembled here rather than inside the modal so the preview and the ladder
   * at the top of the card are reading the same estimate — a margin previewed
   * off a different set of costs is a margin the job cannot hit.
   */
  const ladderInput = useMemo(
    () =>
      estimate && children
        ? ({
            estimate,
            items: children.items,
            changeOrders: children.changeOrders,
            materialOrders: children.materialOrders,
            workOrders: children.workOrders,
            expenses: children.expenses,
            returns: children.returns,
            jobType: job.job_type,
            place: job,
            signature,
            layouts: layoutsQuery.data ?? [],
            autoExpenseOverrides: overridesQuery.data?.overrides ?? null,
            autoExpenseRemovals: overridesQuery.data?.removals ?? null,
            workOrderCrews: children.workOrders.map((o) => o.crew ?? null),
            // Insurance budgeted by hand is a cost like any other, and a
            // margin is only a margin if it is taken on all of it. Left out,
            // an estimate with no work orders yet would be repriced to a
            // number that read 40% and earned nothing on the liability.
            insuranceLines: children.insuranceLines ?? [],
          } satisfies LadderInput)
        : null,
    [
      estimate,
      children,
      job,
      signature,
      layoutsQuery.data,
      overridesQuery.data,
    ],
  );

  if (estimatesQuery.loading || reference.loading) {
    return <div className="block" />;
  }

  // A job with no estimate opens straight into the choice. Clicking "Estimate"
  // on the checklist lands here, and a button that only opens another box
  // would be a step for nothing. Offered once, so closing it stays closed.
  if (!estimate && !creating && !offered) {
    setOffered(true);
    setCreating(true);
  }

  if (!estimate) {
    return (
      <>
        <div className="block">
          <Stub
            title={t("No estimate yet")}
            message={t(
              "Build the contract for this job — line items, change orders, and the cost roll-up.",
            )}
          />
          <div style={{ textAlign: "center" }}>
            <button className="addrow" onClick={() => setCreating(true)}>
              {t("+ Create estimate")}
            </button>
          </div>
        </div>
        {creating && (
          <CreateEstimateModal
            job={job}
            existingCount={estimates.length}
            products={reference.data?.products ?? []}
            suppliers={reference.data?.suppliers ?? []}
            trades={trades.map((trade) => trade.name)}
            onClose={() => setCreating(false)}
            onOpenMeasurements={
              onOpenMeasurements &&
              (() => {
                setCreating(false);
                onOpenMeasurements();
              })
            }
            onCreated={async (id) => {
              setCreating(false);
              setSelectedId(id);
              await estimatesQuery.reload();
              await flagQuery.reload();
              await onEstimateCreated?.();
            }}
          />
        )}
      </>
    );
  }

  if (!children || !totals) return <div className="block" />;

  /*
   * The catalogue, less what this roof must not be sold.
   *
   * Jonathon, 2026-08-27: the GAF Master Flow pipe boots are off metal roofs.
   * Filtered here rather than in each picker, because this one list is what
   * the line modal, the work order editor and the material order editor all
   * choose from — three places to forget instead of one.
   */
  const products = productsForRoofType(
    reference.data?.products ?? [],
    estimate.roof_type,
  );
  const tradeNames = trades.map((trade) => trade.name);
  /** Which rule, if any, is keeping this estimate on the job. */
  const deleteBlocker = estimateDeleteBlocker(
    job,
    estimate,
    paidOrders,
    documents,
  );
  /** The links the customer can still open, each one voidable on its own. */
  const openDocuments = liveDocuments(documents);

  /**
   * Change the estimate row itself, and repaint the copy on screen.
   *
   * That second half is the whole point. `reload()` refetches an estimate's
   * children — its lines, its orders, its expenses — and never the estimate,
   * so anything written here that waited on a reload appeared to save and then
   * sprang back to its old value.
   */
  const saveEstimate = (patch: Partial<Estimate>) =>
    guard(async () => {
      await updateRow("estimates", estimate.id, patch);
      estimatesQuery.setData(
        estimates.map((e) => (e.id === estimate.id ? { ...e, ...patch } : e)),
      );
    });

  const saveEstimateField = (field: string, raw: string) =>
    saveEstimate({
      [field]: NUMERIC_ESTIMATE_FIELDS.has(field)
        ? Number.parseFloat(raw.replace(/[$,\s]/g, "")) || 0
        : raw === ""
          ? null
          : raw,
    } as Partial<Estimate>);

  /** The trade trump: set once, written to every line item and change order. */
  /**
   * Set the margin and let the price follow.
   *
   * The rep says what gross profit they want; `planReprice` works out the
   * contract that produces it — allowing for the expenses that are themselves
   * a slice of revenue — and what every line item becomes. Change orders and
   * discounts are left alone: they are agreed numbers.
   *
   * The whole thing used to be a `window.prompt` and three `window.alert`s.
   * Jonathon, 2026-09-15: "often times sam tries to adjust it but it won't let
   * him." See `MarginModal` for the four different ways that could be true.
   */
  /**
   * Write what the plan worked out.
   *
   * Nothing is decided here: `planReprice` already said what every line
   * becomes, and this is the loop that saves it. That separation is what lets
   * the preview in the modal be the same number that lands in the database.
   */
  const applyReprice = (plan: RepricePlan) =>
    guard(async () => {
      for (const line of plan.lines) {
        const patch: Record<string, unknown> = {
          customer_price: toDecimal(line.customerPrice),
        };
        if (line.unitPrice !== null) patch.unit_price = line.unitPrice;
        await updateRow("estimate_items", line.id, patch);
      }
      await reload();
      setSettingMargin(false);
    });

  const applyTradeTrump = (value: string) =>
    guard(async () => {
      await updateRow("estimates", estimate.id, { trade: value || null });
      if (value) {
        await Promise.all([
          supabaseUpdateAll("estimate_items", "estimate_id", estimate.id, {
            trade: value,
          }),
          supabaseUpdateAll("change_orders", "estimate_id", estimate.id, {
            trade: value,
          }),
        ]);
      }
      await estimatesQuery.reload();
      // A new trade can move the multi-option default (ADR 0120).
      await flagQuery.reload();
      await reload();
    });

  return (
    <>
      <ErrorNote error={error} />

      <div className="estsel">
        {estimates.map((e) => (
          <span
            key={e.id}
            className={`estchip${e.id === estimate.id ? " viewing" : ""}${
              e.is_active ? " live" : ""
            }`}
            title={
              e.offer_on_proposal === false
                ? t("Not offered to the customer — a working estimate")
                : e.is_active
                  ? t("This is the estimate the job is priced on")
                  : t("Not the active estimate")
            }
            onClick={() => setSelectedId(e.id)}
          >
            {/* The option's own name: what the first line is called, when
                nobody has typed a package name (Jonathon, 2026-09-15). Three
                tabs reading "Estimate 1", "Estimate 2", "Estimate 3" said
                nothing about which one was the metal roof. */}
            {optionLabels.get(e.id) ?? estimateOptionLabel(e)}{" "}
            <span className="v">v{e.version}</span>
            {e.is_active ? " ★" : ""}
            {/* Which ones a customer would be offered has to be readable at a
                glance, or a rep finds out what is in the packet by sending it. */}
            {e.offer_on_proposal === false ? (
              <span className="v"> · {t("not offered")}</span>
            ) : null}
          </span>
        ))}
        <button
          className="addrow ghost"
          style={{ marginTop: 0 }}
          onClick={() => setCreating(true)}
        >
          {t("+ New estimate")}
        </button>
        {/* A toggle, not a one-way door. Pinning is the only rung of the rule a
            person sets by hand, so it has to be one they can take back: on a
            multi-option job nothing un-pins the others for you, and without an
            un-pin a job that has had two options pinned can never go back to
            selling one. */}
        {(!estimate.is_active || estimate.active_pinned) && (
          <button
            className="addrow ghost"
            style={{ marginTop: 0 }}
            title={
              estimate.active_pinned
                ? t(
                    "Stop pinning this one and let the rule decide again — signed, then pinned, then cheapest",
                  )
                : t("Pin this estimate as the one the job is priced on")
            }
            onClick={() =>
              void guard(async () => {
                // Pin it rather than setting is_active directly. Active is
                // worked out — signed, then pinned, then cheapest — and writing
                // the answer instead of the reason would last exactly until the
                // next price change.
                await updateRow("estimates", estimate.id, {
                  active_pinned: !estimate.active_pinned,
                });
                await estimatesQuery.reload();
                await reload();
              })
            }
          >
            {estimate.active_pinned ? t("Un-pin") : t("Make active")}
          </button>
        )}
        {/* Whether the customer is offered this one as a package.

            Jonathon, 2026-08-15: "on the estimate builder we will toggle off or
            delete the estimates we don't want to present." So the decision is
            made here, beside the estimate it is about, and Present Estimate
            reports what it is going to send rather than asking again.

            Only shown once there is more than one estimate, because with one
            there is nothing to choose between and turning the only estimate off
            would just be a way to confuse yourself. Nothing is enforced by it:
            `proposal_options` records what a document actually offered and is
            frozen at send, so toggling a package off never changes a packet a
            customer is already reading. */}
        {estimates.length > 1 && (
          <button
            className="addrow ghost"
            style={{ marginTop: 0 }}
            title={
              estimate.offer_on_proposal === false
                ? t("Put this back in the packet the customer chooses from")
                : t(
                    "Keep this one out of the packet — a working estimate, not one to offer",
                  )
            }
            onClick={() =>
              void guard(async () => {
                await updateRow("estimates", estimate.id, {
                  offer_on_proposal: estimate.offer_on_proposal === false,
                });
                await estimatesQuery.reload();
              })
            }
          >
            {estimate.offer_on_proposal === false
              ? t("Offer to customer")
              : t("Do not offer")}
          </button>
        )}
        {/* Why the delete button is not there.

            A missing button explains nothing, and one of the five reasons is
            something somebody can act on: a document the customer can still
            open is undone by voiding it, and then the estimate goes. The
            database refuses every one of them either way — this is the card
            saying so before a Postgres exception does. */}
        {paidOrders.length > 0 && !jobIsWon(job) && (
          <span className="na-hint" style={{ alignSelf: "center" }}>
            {t("Cannot be deleted — {n} order(s) already paid", {
              n: paidOrders.length,
            })}
          </span>
        )}
        {deleteBlocker === "live_document" && (
          <span className="na-hint" style={{ alignSelf: "center" }}>
            {t(
              "Sent to the customer — void it below to clear this estimate away",
            )}
          </span>
        )}
        {deleteBlocker === "was_signed" && (
          <span className="na-hint" style={{ alignSelf: "center" }}>
            {t(
              "Signed once, so it is kept — leave it out of the packet instead",
            )}
          </span>
        )}
        {/* Unwinding what was sent.

            Jonathon, 2026-08-19: "we need to be able to unwind estimates that
            are sent so we can delete estimates in order to keep their estimate
            builder clean." One button per live document, because an estimate
            offered twice is two links and killing one of them is not the job.
            The customer's copy survives — voiding takes away its force, not the
            record of it (ADR 0047). */}
        {openDocuments.map((doc) => (
          <button
            key={doc.id}
            className="addrow ghost danger"
            style={{ marginTop: 0 }}
            disabled={voiding === doc.id}
            title={t(
              "Kill the link the customer was sent, so this estimate can be changed or deleted",
            )}
            onClick={() =>
              void guard(async () => {
                const warning = doc.signed
                  ? t("This document has been signed.") +
                    "\n\n" +
                    t(
                      "Voiding it takes away its force — the signed record is kept, but the estimate stands back down and a new document has to be sent.",
                    ) +
                    "\n\n" +
                    t("Void it anyway?")
                  : t('Void "{title}" sent to the customer?', {
                      title: doc.title ?? t("the document"),
                    }) +
                    "\n\n" +
                    t(
                      "Their link stops working. This cannot be undone — present the estimate again to send a fresh one.",
                    );
                if (!window.confirm(warning)) return;
                setVoiding(doc.id);
                try {
                  await voidProposal(doc.id);
                  await documentsQuery.reload();
                  await estimatesQuery.reload();
                  await reload();
                } finally {
                  setVoiding(null);
                }
              })
            }
          >
            {voiding === doc.id ? t("Voiding…") : t("Void what was sent")}
          </button>
        ))}
        {deleteBlocker === null && (
          <button
            className="addrow ghost danger"
            style={{ marginTop: 0 }}
            onClick={() => {
              // Say what goes with it. The orders belong to this estimate now,
              // so deleting it takes them, and finding that out afterwards is
              // not the moment to learn how the scoping works.
              const going = [
                children.items.length &&
                  t("{n} line(s)", { n: children.items.length }),
                children.workOrders.length &&
                  t("{n} work order(s)", { n: children.workOrders.length }),
                children.materialOrders.length &&
                  t("{n} material order(s)", {
                    n: children.materialOrders.length,
                  }),
                children.expenses.length &&
                  t("{n} expense(s)", { n: children.expenses.length }),
                children.changeOrders.length &&
                  t("{n} change order(s)", { n: children.changeOrders.length }),
              ].filter(Boolean) as string[];

              const what = going.length
                ? "\n\n" +
                  t("This also deletes its {list}.", { list: going.join(", ") })
                : "";
              if (
                !window.confirm(
                  t('Delete "{name}" (v{version})?', {
                    name: estimate.name ?? t("Estimate"),
                    version: estimate.version,
                  }) +
                    what +
                    "\n\n" +
                    t("This cannot be undone."),
                )
              ) {
                return;
              }

              void guard(async () => {
                await deleteRow("estimates", estimate.id);
                // Let whichever estimate is now active decide itself.
                setSelectedId(null);
                await estimatesQuery.reload();
                await flagQuery.reload();
                await reload();
              });
            }}
          >
            {t("Delete estimate")}
          </button>
        )}
      </div>

      {estimates.length > 1 && (
        <label className="estmulti">
          <input
            type="checkbox"
            checked={multiOption}
            onChange={(e) => {
              const multi_option = e.target.checked;
              void guard(async () => {
                /*
                 * `multi_option_manual` alongside it, always.
                 *
                 * The database sets this box for itself while nobody has said
                 * otherwise — on for a roof and the gutters, off for two roofs
                 * (ADR 0120). Ticking it by hand is the answer, so this is what
                 * stops the next edit to an estimate quietly setting it back.
                 */
                const patch = { multi_option, multi_option_manual: true };
                // Through the parent, so the job this card is rendering from
                // changes too. Writing the row on its own leaves the box
                // showing the old answer until the job is reopened.
                if (onPatchJob) await onPatchJob(patch);
                else await updateRow("jobs", job.id, patch);
                // Turning it on or off re-decides which estimates are active.
                await estimatesQuery.reload();
                await flagQuery.reload();
                await reload();
              });
            }}
          />
          <span>
            {t("Customer can buy more than one of these")}
            {/* Said out loud, because the box now arrives with an answer in it
                and a rep should know why. */}
            <small>
              {/* Set by hand and on, two roofs can be bought together — a
                  house and a detached garage (ADR 0124) — and the box looks
                  the same as when it set itself, so the sentence says which
                  it is, and what re-ticking it by hand would do. */}
              {t(multiOptionHint(multiOption, multiOptionManual))}
            </small>
          </span>
        </label>
      )}

      <TotalsStrip
        totals={totals}
        onEditMargin={() => setSettingMargin(true)}
      />

      <JobTotals options={jobOptions} openEstimateId={estimate.id} />

      <MirrorSection
        onEnter={() => mirror.begin("items")}
        onLeave={() => mirror.end("items")}
      >
        <Builder
          job={job}
          estimate={estimate}
          label={
            optionLabels.get(estimate.id) ??
            estimateOptionLabel(estimate, children.items)
          }
          totals={totals}
          items={children.items}
          payments={children.payments}
          tradeNames={tradeNames}
          onSaveField={saveEstimateField}
          onSaveEstimate={saveEstimate}
          onTradeTrump={applyTradeTrump}
          onEditMargin={() => setSettingMargin(true)}
          onEditLine={setEditingLine}
          onShowAdditions={() => setShowAdditions(true)}
          onPresent={() => setPresenting(true)}
          unsignedWarning={unsignedChangeOrderWarning(children.changeOrders)}
          onInvoice={() => setInvoicing(true)}
          additions={additions}
          onReload={reload}
          onGuard={guard}
          materialOrderCount={children.materialOrders.length}
          workOrderCount={children.workOrders.length}
          expenseCount={
            children.expenses.length +
            activeAutoExpenses(totals.autoExpenses).length
          }
          // What an estimate that has never answered the financing question
          // actually does — the same setting Present resolves against.
          showFinancingDefault={
            companyQuery.data?.show_financing_default ?? null
          }
          // The company-wide permission. Off until Foundation Finance approves,
          // and it beats the estimate's own setting.
          financingOffered={companyQuery.data?.financing_offered ?? null}
          companyFinancingTerms={{
            termMonths: companyQuery.data?.financing_term_months ?? null,
            apr: numberOrNull(companyQuery.data?.financing_apr),
          }}
        />
      </MirrorSection>

      {presenting && (
        <PresentEstimateModal
          job={job}
          estimate={estimate}
          estimates={estimates}
          layouts={layoutsQuery.data ?? []}
          items={children.items}
          payments={children.payments}
          totals={totals}
          company={companyQuery.data ?? null}
          repName={job.rep?.full_name ?? null}
          repPhone={job.rep?.phone ?? null}
          gmailAddress={gmailAccount?.email_address ?? null}
          // Whether the answer is known yet, and why not when it is not. A
          // send button that is dead for an unsaid reason is the thing that
          // stopped Lysa (2026-09-08).
          gmailLoading={gmailLoading}
          gmailError={gmailError ?? sendersError}
          senders={gmailSenders}
          ownSender={ownGmailSender}
          onClose={() => setPresenting(false)}
          onPresented={async () => {
            await reload();
            await onPresented?.();
          }}
        />
      )}

      {presentingChangeOrder && (
        <PresentChangeOrderModal
          job={job}
          estimate={estimate}
          changeOrders={children.changeOrders}
          totals={totals}
          company={companyQuery.data ?? null}
          companyLoading={companyQuery.loading}
          repName={job.rep?.full_name ?? null}
          repPhone={job.rep?.phone ?? null}
          gmailAddress={gmailAccount?.email_address ?? null}
          gmailLoading={gmailLoading}
          gmailError={gmailError ?? sendersError}
          senders={gmailSenders}
          ownSender={ownGmailSender}
          onClose={() => setPresentingChangeOrder(false)}
          onPresented={reload}
        />
      )}

      {invoicing && (
        <InvoiceModal
          job={job}
          estimate={estimate}
          // So a job sold twice can be billed once: the modal ticks the
          // estimates to bill, and names them the way these tabs do.
          estimates={estimates}
          labels={optionLabels}
          items={children.items}
          changeOrders={children.changeOrders}
          onClose={() => setInvoicing(false)}
        />
      )}

      {showAdditions && (
        <AdditionsModal
          additions={additions}
          onClose={() => setShowAdditions(false)}
        />
      )}

      {editingLine && (
        <EstimateLineModal
          item={editingLine}
          products={products}
          trades={tradeNames}
          // So a measured product — gutters, drip edge, ridge — can work its
          // own quantity out of the roof instead of being counted by hand.
          measurements={layoutsQuery.data ?? null}
          onClose={() => setEditingLine(null)}
          onSave={(patch) =>
            guard(async () => {
              await updateRow("estimate_items", editingLine.id, patch);
              // A deck over is not just a line: it raises its own Home Depot
              // order, pays the crew per sheet and cancels the patch-repair
              // allowance the template carried.
              if (
                products.find((p) => p.id === patch.product_id)?.name ===
                DECK_OVER_PRODUCTS.line
              ) {
                await applyDeckOver(editingLine.id);
              }
              await reload();
            })
          }
        />
      )}

      <div className="block esec sec-work" ref={workSection}>
        <h2>{t("Work Orders")}</h2>
        <div className="ewrap">
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Order #")}</th>
                <th>{t("Crew")}</th>
                <th>{t("Trades")}</th>
                <th>{t("Start")}</th>
                <th>{t("End")}</th>
                <th>{t("Status")}</th>
                <th className="num">{t("Total")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {children.workOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tnone">
                    {t("No work orders.")}
                  </td>
                </tr>
              ) : (
                children.workOrders.map((order) => (
                  <tr key={order.id}>
                    <td>WO-{order.order_number}</td>
                    <td>{crewLabel(order.crew) ?? "—"}</td>
                    <td>
                      {Array.isArray(order.trades)
                        ? order.trades.join(", ")
                        : (order.trades ?? "—")}
                    </td>
                    <td>
                      {order.start_at ? fmtDateTime(order.start_at) : "—"}
                    </td>
                    <td>{order.end_at ? fmtDateTime(order.end_at) : "—"}</td>
                    <td>
                      <span className={`badge ${order.status}`}>
                        {t(order.status)}
                      </span>
                    </td>
                    <td className="num">{formatCents(documentTotal(order))}</td>
                    <td>
                      <button
                        className="addrow ghost"
                        style={{ marginTop: 0 }}
                        onClick={() => {
                          mirror.begin("work", order.id);
                          setEditingWorkOrder(order.id);
                        }}
                      >
                        {t("Edit")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Three ways to get one, beside each other. Empty and typed out by
            hand; worked out from the roof off a template, with none of the
            template's wording; or copied off one of this job's other estimates.
            Jonathon asked for the second and third on 2026-08-24. */}
        <div className="ordermakers">
          <button
            className="addrow"
            onClick={() =>
              void guard(async () => {
                const created = await insertRow<{ id: string }>("work_orders", {
                  job_id: job.id,
                  estimate_id: estimate.id,
                  status: "not_scheduled",
                  tax_rate: 0,
                  created_by: profileId,
                });
                await reload();
                mirror.begin("work", created.id);
                setEditingWorkOrder(created.id);
              })
            }
          >
            {t("+ Add Work Order")}
          </button>
          <button
            className="addrow ghost"
            onClick={() => setGenerating("work")}
          >
            {t("From Measurements")}
          </button>
          <button className="addrow ghost" onClick={() => setCopying("work")}>
            {t("Copy From Another Estimate")}
          </button>
        </div>
      </div>

      <div className="block esec sec-material">
        <h2>{t("Material Orders")}</h2>
        <div className="ewrap">
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Order #")}</th>
                <th>{t("Supplier")}</th>
                <th>{t("Trade")}</th>
                <th>{t("Delivery")}</th>
                <th>{t("Status")}</th>
                <th className="num">{t("Subtotal")}</th>
                <th className="num">{t("Tax Rate")}</th>
                <th className="num">{t("Tax")}</th>
                <th className="num">{t("Total")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {children.materialOrders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="tnone">
                    {t("No material orders.")}
                  </td>
                </tr>
              ) : (
                children.materialOrders.map((order) => (
                  <tr key={order.id}>
                    <td>MO-{order.order_number}</td>
                    <td>{order.supplier?.name ?? "—"}</td>
                    <td>{order.trade ?? "—"}</td>
                    <td>{fmtDate(order.delivery_date)}</td>
                    <td>
                      <span className={`badge ${order.status}`}>
                        {t(order.status)}
                      </span>
                    </td>
                    <td className="num">
                      {formatCents(documentSubtotal(order))}
                    </td>
                    <td className="num">
                      <NumberCell
                        value={order.tax_rate}
                        width={60}
                        onSave={(v) =>
                          void guard(async () => {
                            // A new rate clears a typed amount (ADR 0243).
                            await updateRow("material_orders", order.id, {
                              tax_rate: v,
                              tax_amount: null,
                            });
                            await reload();
                          })
                        }
                      />{" "}
                      %
                    </td>
                    <td className="num">{formatCents(documentTax(order))}</td>
                    <td className="num">{formatCents(documentTotal(order))}</td>
                    <td>
                      <button
                        className="addrow ghost"
                        style={{ marginTop: 0 }}
                        onClick={() => {
                          mirror.begin("material", order.id);
                          setEditingMaterialOrder(order.id);
                        }}
                      >
                        {t("Edit")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="ordermakers">
          <button
            className="addrow"
            onClick={() =>
              void guard(async () => {
                const created = await insertRow<{ id: string }>(
                  "material_orders",
                  {
                    job_id: job.id,
                    estimate_id: estimate.id,
                    status: "not_placed",
                    tax_rate: DEFAULT_MATERIAL_TAX_RATE,
                    trade: estimate.trade,
                    created_by: profileId,
                  },
                );
                await reload();
                mirror.begin("material", created.id);
                setEditingMaterialOrder(created.id);
              })
            }
          >
            {t("+ Add Material Order")}
          </button>
          <button
            className="addrow ghost"
            onClick={() => setGenerating("material")}
          >
            {t("From Measurements")}
          </button>
          <button
            className="addrow ghost"
            onClick={() => setCopying("material")}
          >
            {t("Copy From Another Estimate")}
          </button>
        </div>
      </div>

      {generating && (
        <GenerateOrderModal
          job={job}
          estimateId={estimate.id}
          kind={generating}
          trade={estimate.trade}
          suppliers={reference.data?.suppliers ?? []}
          onClose={() => setGenerating(null)}
          onCreated={async (orderId) => {
            const kind = generating;
            setGenerating(null);
            await reload();
            if (kind === "material") setEditingMaterialOrder(orderId);
            else setEditingWorkOrder(orderId);
          }}
        />
      )}

      {copying && (
        <CopyFromEstimateModal
          job={job}
          estimate={estimate}
          estimates={estimates}
          bucket={copying}
          optionLabels={optionLabels}
          onClose={() => setCopying(null)}
          onCopied={async (orderId) => {
            const bucket = copying;
            setCopying(null);
            await reload();
            if (!orderId) return;
            if (bucket === "material") setEditingMaterialOrder(orderId);
            if (bucket === "work") setEditingWorkOrder(orderId);
          }}
        />
      )}

      <MirrorSection
        onEnter={() => mirror.begin("expenses")}
        onLeave={() => mirror.end("expenses")}
      >
        <Expenses
          job={job}
          estimate={estimate}
          rows={children.expenses}
          tradeNames={tradeNames}
          total={totals.otherExpenses}
          autoExpenses={totals.autoExpenses}
          insuranceLines={children.insuranceLines ?? []}
          crews={reference.data?.crews ?? []}
          crewWorkOrders={children.workOrders.map((o) => ({
            crewId: o.crew_id ?? null,
          }))}
          layouts={layoutsQuery.data ?? []}
          onReload={reload}
          onOverridesChanged={overridesQuery.reload}
          onGuard={guard}
          onCopyFromEstimate={() => setCopying("expenses")}
          onSaveEstimate={saveEstimate}
        />
      </MirrorSection>

      <Returns
        job={job}
        estimate={estimate}
        rows={children.returns}
        products={products}
        suppliers={reference.data?.suppliers ?? []}
        total={totals.returns}
        onReload={reload}
        onGuard={guard}
      />

      <ChangeOrders
        estimate={estimate}
        job={job}
        rows={children.changeOrders}
        documents={changeOrderDocs}
        products={products}
        tradeNames={tradeNames}
        onReload={reload}
        onGuard={guard}
        onPresent={() => setPresentingChangeOrder(true)}
      />

      {editingWorkOrder && (
        <WorkOrderEditor
          orderId={editingWorkOrder}
          products={products}
          onClose={() => {
            // Closing the editor is "clicking off" the work order, which is
            // when the same edits are offered to the other estimates.
            mirror.end("work", editingWorkOrder);
            setEditingWorkOrder(null);
          }}
          onSaved={reload}
        />
      )}
      {editingMaterialOrder && (
        <MaterialOrderEditor
          orderId={editingMaterialOrder}
          products={products}
          suppliers={reference.data?.suppliers ?? []}
          onClose={() => {
            mirror.end("material", editingMaterialOrder);
            setEditingMaterialOrder(null);
          }}
          onSaved={reload}
        />
      )}
      {mirror.node}
      {/* The margin, and what setting it does, before it does it. */}
      {settingMargin && ladderInput && children && totals && (
        <MarginModal
          input={ladderInput}
          totals={totals}
          lines={children.items}
          current={totals.margin}
          onClose={() => setSettingMargin(false)}
          onApply={applyReprice}
        />
      )}
      {creating && (
        <CreateEstimateModal
          job={job}
          existingCount={estimates.length}
          products={products}
          suppliers={reference.data?.suppliers ?? []}
          trades={tradeNames}
          onClose={() => setCreating(false)}
          onOpenMeasurements={
            onOpenMeasurements &&
            (() => {
              setCreating(false);
              onOpenMeasurements();
            })
          }
          onCreated={async (id) => {
            setCreating(false);
            setSelectedId(id);
            await estimatesQuery.reload();
            await flagQuery.reload();
            await reload();
            await onEstimateCreated?.();
          }}
        />
      )}
    </>
  );
}

/** Bulk update helper — used by the trade trump. */
async function supabaseUpdateAll(
  table: string,
  column: string,
  value: string,
  patch: Record<string, unknown>,
) {
  const { error } = await supabase.from(table).update(patch).eq(column, value);
  if (error) throw new Error(error.message);
}

/**
 * What was added after the job went on the calendar.
 *
 * This is a reporting screen, not an editing one — the rows live on their own
 * documents and are changed there. What it is for is the conversation after the
 * fact: eight sheets of decking and a crane means the estimate was short, and
 * the only way anyone notices a pattern is if the late arrivals are listed
 * apart from everything else.
 */
function AdditionsModal({
  additions,
  onClose,
}: {
  additions: Additions;
  onClose: () => void;
}) {
  const kindLabel: Record<string, string> = {
    material: t("Material"),
    labor: t("Labor"),
    expense: t("Expense"),
  };

  return (
    <Modal
      title={t("Added After Scheduling")}
      subtitle={
        additions.scheduledAt
          ? t(
              "Everything that landed on this job after it went on the calendar on {date}.",
              {
                date: fmtDateTime(additions.scheduledAt),
              },
            )
          : t("This job has never been scheduled.")
      }
      onClose={onClose}
      actions={
        <button className="btn-primary" onClick={onClose}>
          {t("Close")}
        </button>
      }
    >
      <div className="totrow sub">
        <span className="lbl">{t("Material")}</span>
        <span className="num">{formatCents(additions.material)}</span>
        <span className="num" />
        <span className="num" />
      </div>
      <div className="totrow sub">
        <span className="lbl">{t("Labor")}</span>
        <span className="num">{formatCents(additions.labor)}</span>
        <span className="num" />
        <span className="num" />
      </div>
      <div className="totrow sub">
        <span className="lbl">{t("Other Expenses")}</span>
        <span className="num">{formatCents(additions.expenses)}</span>
        <span className="num" />
        <span className="num" />
      </div>
      <div className="totrow strong">
        <span className="lbl">
          <b>{t("Total")}</b>
        </span>
        <span className="num overrun">
          <b>{formatCents(additions.total)}</b>
        </span>
        <span className="num" />
        <span className="num" />
      </div>

      <div className="ewrap" style={{ marginTop: 16 }}>
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Added")}</th>
              <th>{t("Kind")}</th>
              <th>{t("Item")}</th>
              <th>{t("Document")}</th>
              <th className="num">{t("Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {additions.rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="tnone">
                  {additions.scheduledAt
                    ? t("Nothing was added after this job was scheduled.")
                    : t(
                        "Put the job on the calendar and additions will be tracked from that point.",
                      )}
                </td>
              </tr>
            ) : (
              additions.rows.map((row, i) => (
                <tr key={`${row.document}:${row.addedAt}:${i}`}>
                  <td className="sub">{fmtDateTime(row.addedAt)}</td>
                  <td className="sub">{kindLabel[row.kind] ?? row.kind}</td>
                  <td>{row.description}</td>
                  <td className="sub">{t(row.document)}</td>
                  <td className="num">{formatCents(row.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/** The shape a just-inserted row has, before the modal fills it in. */
const blankLine: EstimateItem = {
  id: "",
  estimate_id: "",
  product_id: null,
  trade: null,
  description: null,
  unit: "SQ",
  qty: 1,
  customer_price: 0,
  unit_price: null,
  discount: 0,
  is_upgrade: false,
  work_doing: true,
  work_completed: false,
  sort_order: 0,
  note: null,
  color: null,
};

/**
 * Whether this job makes money, before you scroll.
 *
 * The four numbers that decide it were at the foot of a five-section page, so
 * answering "is this any good" meant scrolling past every line item on the way
 * down and again on the way back. Jonathon, 2026-08-07, picked this out of a
 * list of options for the estimate page.
 *
 * Every figure comes from `computeLadder` — nothing here does arithmetic, and
 * the strip cannot disagree with the totals at the bottom because it is reading
 * the same object.
 */
function TotalsStrip({
  totals,
  onEditMargin,
}: {
  totals: LadderTotals;
  /** Opens the margin form — the same one as the Gross Profit row's button. */
  onEditMargin: () => void;
}) {
  // Below the target and it is worth knowing at a glance rather than working it
  // out. Not a warning — plenty of jobs are sold under it deliberately.
  const thin = totals.margin < TARGET_MARGIN;

  return (
    <div className="tstrip">
      <div className="tcell">
        <span className="k">{t("Contract")}</span>
        <span className="v">{formatCents(totals.grandTotal)}</span>
      </div>
      <div className="tcell">
        <span className="k">{t("Cost")}</span>
        <span className="v">{formatCents(totals.totalCost)}</span>
      </div>
      <div className="tcell">
        <span className="k">{t("Gross Profit")}</span>
        <span className="v">{formatCents(totals.grossProfit)}</span>
      </div>
      {/* The margin is set from here too. The only other way in is a button
          in the totals at the foot of the page, and a phone hid that column
          altogether — Jonathon, 2026-10-15: "we can't adjust the margin on our
          phone apps." This one is at the top, on every screen. */}
      <button
        type="button"
        className={`tcell tedit${thin ? " thin" : ""}`}
        onClick={onEditMargin}
      >
        <span className="k">
          {t("Margin")}{" "}
          <span className="pen" aria-hidden="true">
            ✎
          </span>
        </span>
        <span className="v">
          {formatPercent(totals.margin)}
          <span className="agp">
            {" "}
            · {formatPercent(totals.agpMargin)} {t("AGP")}
          </span>
        </span>
        <span className="sr-only"> — {t("change the margin")}</span>
      </button>
    </div>
  );
}

function Builder({
  job,
  estimate,
  label,
  totals,
  items,
  payments,
  tradeNames,
  onSaveField,
  onSaveEstimate,
  onTradeTrump,
  onEditMargin,
  onEditLine,
  onShowAdditions,
  onPresent,
  onInvoice,
  additions,
  unsignedWarning,
  onReload,
  onGuard,
  materialOrderCount,
  workOrderCount,
  expenseCount,
  showFinancingDefault,
  financingOffered,
  companyFinancingTerms,
}: {
  job: Job;
  estimate: Estimate;
  /** What the tab above calls this estimate — `estimateOptionLabels`. */
  label: string;
  totals: LadderTotals;
  items: NonNullable<
    Awaited<ReturnType<typeof fetchEstimateChildren>>
  >["items"];
  payments: NonNullable<
    Awaited<ReturnType<typeof fetchEstimateChildren>>
  >["payments"];
  tradeNames: string[];
  onSaveField: (field: string, value: string) => Promise<void>;
  onSaveEstimate: (patch: Partial<Estimate>) => Promise<void>;
  onTradeTrump: (value: string) => Promise<void>;
  onEditMargin: () => void;
  onEditLine: (item: EstimateItem) => void;
  onShowAdditions: () => void;
  onPresent: () => void;
  onInvoice: () => void;
  additions: Additions;
  /**
   * How much of the grand total nobody has signed for, in a sentence, or
   * null when there is nothing to say.
   *
   * Passed in rather than worked out here because the change orders live
   * one component up. It reads under the grand total on purpose: the money
   * is already inside that figure (Jonathon, 2026-09-19 — a change order
   * counts from the moment it is typed), so the only honest place to say so
   * is beside the number it is inside.
   */
  unsignedWarning: string | null;
  onReload: () => Promise<void>;
  onGuard: (action: () => Promise<void>) => Promise<void>;
  materialOrderCount: number;
  workOrderCount: number;
  expenseCount: number;
  /** `company_settings.show_financing_default`, for an estimate that is null. */
  showFinancingDefault: boolean | null;
  /** `company_settings.financing_offered` — false hides the row entirely. */
  financingOffered: boolean | null;
  /**
   * The term and the rate the company sets once, in Tools. They fill the
   * placeholders on this estimate's boxes and are what it is presented with
   * until somebody types something else (ADR 0201).
   */
  companyFinancingTerms: { termMonths: number | null; apr: number | null };
}) {
  const totalRow = (
    label: string,
    value: string,
    extra?: [string?, React.ReactNode?],
    cls?: string,
  ) => (
    <div className={`totrow ${cls ?? ""}`} key={label}>
      <span className="lbl" dangerouslySetInnerHTML={{ __html: label }} />
      <span className="num" dangerouslySetInnerHTML={{ __html: value }} />
      <span className="num">{extra?.[0] ?? ""}</span>
      <span className="num">{extra?.[1] ?? ""}</span>
    </div>
  );

  return (
    <div className="block esec sec-estimate">
      <h2>
        {t("Estimate Builder")}{" "}
        <span className={`badge ${estimate.status}`}>{t(estimate.status)}</span>
        <span className="n">
          {label} v{estimate.version}
        </span>
      </h2>

      <div className="cprev">
        <div>
          <div className="chk-sub">{t("Contract Preview")}</div>
          <Row label={t("Customer Name")}>{job.contact?.full_name ?? "—"}</Row>
          <Row label={t("Job Address")}>{job.address ?? "—"}</Row>
          {/* What the customer reads above this package's price.

              Jonathon, 2026-08-26: "i also need to be able to rename the
              packages when i need to so i can make it relevant." It is the
              estimate's own name — the builder tab, the list, the proposal
              heading are one string, because a package called something else
              on the customer's copy than in the office is how two people end
              up talking about different quotes. Left blank, or left as
              "Estimate 2", the proposal reads the first line item's name, and
              falls back to "Your New Roof" only when that has none either
              (Jonathon, 2026-09-15). */}
          <Row label={t("Package name")}>
            <TextCell
              value={estimate.name}
              onSave={(v) => void onSaveField("name", v)}
            />
            <div className="na-hint" style={{ marginTop: 4 }}>
              {t(
                'Blank reads as the first line item\'s name — or "Your New Roof", "Your Repair" on a repair, when that has no name either.',
              )}
            </div>
          </Row>
          {/* Four rows used to stand here: shingle / material, colour,
              delivery instructions and existing damage. They came off the
              Present screen in August and Jonathon asked for them off this one
              on 2026-08-25 — they were four boxes nobody filled in, sitting
              above the number the whole card exists to show.

              Nothing downstream asks for them. The colour was never typed here
              anyway: it comes off the line item that carries it, which is the
              colour the material order is placed on. The proposal's colour
              chart falls back to the roof type and then to the line items
              (`shingleLineFor`), and the contract prints "None" for delivery
              and existing damage, which is the ordinary answer rather than an
              omission. The columns are still on `estimates`, so a future screen
              that wants them has somewhere to write. */}
          <Row label={t("Discounts")}>
            {formatCents(
              (totals.lineDiscount + totals.officeDiscount) as never,
            )}
          </Row>
          <Row label={t("Total Contract Amount")}>
            <b>{formatCents(totals.grandTotal)}</b>
          </Row>

          {/* The 3% a customer added by paying on a credit card.
              Shown only when there is one, and never as part of the contract:
              it is not work sold, it does not attract the GC fee and it is not
              a rep's to earn on. The processor's actual cut is sitting in Other
              Expenses above, which is what makes the two cancel out. */}
          {totals.cardSurcharge > 0 ? (
            <>
              <Row label={t("Credit card fee (3%)")}>
                {formatCents(totals.cardSurcharge)}
              </Row>
              <Row label={t("Billed to the customer")}>
                <b>{formatCents(totals.customerTotal)}</b>
              </Row>
            </>
          ) : null}

          <PaymentSchedule
            total={totals.grandTotal}
            payments={payments}
            onSaveAmount={(paymentId, amount) =>
              void onGuard(async () => {
                await updateRow("estimate_payments", paymentId, { amount });
                await onReload();
              })
            }
            onSaveTrigger={(paymentId, trigger_text) =>
              void onGuard(async () => {
                await updateRow("estimate_payments", paymentId, {
                  trigger_text,
                });
                await onReload();
              })
            }
            plan={{
              monthlyCents: estimate.plan_monthly_cents ?? null,
              firstDueOn: estimate.plan_first_due_on ?? null,
              // Columns on the estimate row, so `onSaveEstimate`, for the
              // same reason as the financing terms below.
              onSave: (patch) => void onSaveEstimate(patch),
            }}
            financing={{
              show: estimate.show_financing ?? null,
              companyDefault: showFinancingDefault,
              companyOffers: financingOffered,
              jobType: job.job_type,
              // Straight onto the estimate row, which is what `onSaveEstimate`
              // does and `onReload` does not: reload refetches an estimate's
              // children — its lines, its orders, its expenses — and never the
              // estimate itself. So the write landed in the database and the
              // dropdown sprang back to what it had been, every time.
              // Fire and forget: saveEstimate runs through `guard`, which
              // shows its own errors.
              onChange: (show) => void onSaveEstimate({ show_financing: show }),
              terms: {
                downPayment: numberOrNull(estimate.financing_down_payment),
                termMonths: estimate.financing_term_months ?? null,
                apr: numberOrNull(estimate.financing_apr),
              },
              companyTerms: companyFinancingTerms,
              // The same write as the dropdown above, for the same reason:
              // these are columns on the estimate row, and `onReload` refetches
              // an estimate's children rather than the estimate.
              onSaveTerms: (patch) => void onSaveEstimate(patch),
            }}
          />

          {/* The customer-facing document. Everything above it is what the
              office sees; this is the moment it becomes something a person
              outside the company can read and sign. */}
          <button type="button" className="present" onClick={onPresent}>
            {t("Present Estimate")}
          </button>

          {/* The other document a customer reads, and the only one that asks
              for money rather than for a signature. Next to Present because
              they are the same act at two ends of a job (ADR 0068). */}
          <button
            type="button"
            className="addrow ghost"
            style={{ marginTop: 8 }}
            onClick={onInvoice}
          >
            {t("Invoice")}
          </button>
        </div>

        <div>
          <div className="chk-sub">{t("Trade")}</div>
          <Row label={t("Trade (all lines)")}>
            <select
              value={estimate.trade ?? ""}
              onChange={(e) => void onTradeTrump(e.target.value)}
            >
              <option value="">—</option>
              {tradeNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <div className="na-hint" style={{ marginTop: 4 }}>
              {t("Sets this trade on every line item and change order.")}
            </div>
          </Row>

          <div className="chk-sub">{t("Cost Inputs")}</div>
          <Row label={t("Office Discount")}>
            <NumberCell
              value={estimate.office_discount}
              onSave={(v) => void onSaveField("office_discount", String(v))}
            />
          </Row>
          <Row label={t("Discount Note")}>
            <TextCell
              value={estimate.office_discount_note}
              onSave={(v) => void onSaveField("office_discount_note", v)}
            />
          </Row>
          <Row label={t("Job Overhead %")}>
            <NumberCell
              value={estimate.job_overhead_pct}
              variant="plain"
              onSave={(v) => void onSaveField("job_overhead_pct", String(v))}
            />
          </Row>
        </div>
      </div>

      {/* The work, not the wording. Package copy — Scope of Work, Warranty,
          the wood bonus — lives on the estimate as sectioned zero-price rows,
          but showing them here read as clutter and a reporting worry
          (Jonathon, 2026-08-24): a roof replacement option is one line in
          this table. The wording is edited on the Present screen, where the
          customer reads it, and its rows carry no money, so nothing the
          totals or reports sum is hidden by this. */}
      <div className="ewrap" style={{ marginTop: 18 }}>
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Trade")}</th>
              <th>{t("Product")}</th>
              <th className="num">{t("Qty")}</th>
              <th className="num">{t("Customer Price")}</th>
              <th>{t("Work Completed")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {builderItems(items).length === 0 ? (
              <tr>
                <td colSpan={6} className="tnone">
                  {t("No line items yet.")}
                </td>
              </tr>
            ) : (
              builderItems(items).map((item) => (
                <tr key={item.id}>
                  {/* Work Completed acts in place — it is a single fact. What
                      the customer reads is behind Edit, because a scope of work
                      does not fit in a table cell. */}
                  <td className="sub linecell" onClick={() => onEditLine(item)}>
                    {item.trade ?? "—"}
                  </td>
                  {/* Name, colour and the first line of the scope, side by
                      side. They used to stack, which made a row with a scope on
                      it three lines tall. */}
                  <td
                    className="linecell"
                    onClick={() => onEditLine(item)}
                    title={[item.description, item.color, item.note]
                      .filter(Boolean)
                      .join(" — ")}
                  >
                    <div className="rowline">
                      <span className="clink">
                        {item.description || t("(no name)")}
                      </span>
                      {item.color && <span className="sub">{item.color}</span>}
                      {item.note && (
                        <span className="na-hint scopepeek">
                          {item.note.split("\n")[0]}…
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="num linecell" onClick={() => onEditLine(item)}>
                    {Number(item.qty ?? 0)}
                    {item.unit ? ` (${item.unit})` : ""}
                  </td>
                  <td className="num linecell" onClick={() => onEditLine(item)}>
                    {formatCents(toCents(item.customer_price))}
                  </td>
                  <td>
                    <button
                      className={`tog green${item.work_completed ? " on" : ""}`}
                      title={t("Work Completed")}
                      onClick={() =>
                        void onGuard(async () => {
                          await updateRow("estimate_items", item.id, {
                            work_completed: !item.work_completed,
                          });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td className="rowacts">
                    <button
                      className="btn-small"
                      onClick={() => onEditLine(item)}
                    >
                      {t("Edit")}
                    </button>
                    <DeleteButton
                      table="estimate_items"
                      id={item.id}
                      onDone={onReload}
                      onGuard={onGuard}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <button
        className="addrow"
        onClick={() =>
          void onGuard(async () => {
            const created = await insertRow<{ id: string }>("estimate_items", {
              estimate_id: estimate.id,
              trade: estimate.trade,
              qty: 1,
              unit: "SQ",
              customer_price: 0,
              work_doing: true,
              sort_order: nextSort(items),
            });
            await onReload();
            onEditLine({ ...blankLine, ...created } as EstimateItem);
          })
        }
      >
        {t("+ Add Line Item")}
      </button>

      <div style={{ marginTop: 20 }}>
        {totalRow(
          `<b>${t("Totals")}</b>`,
          `<b>${formatCents(totals.lineTotal)}</b>`,
          [
            `${formatCents(totals.grossProfit)}`,
            <span className="pctpill" key="m">
              {formatPercent(totals.margin)}/{formatPercent(totals.agpMargin)}
            </span>,
          ],
          "sub",
        )}
        {totalRow(t("Change Orders"), formatCents(totals.changeOrders))}
        {totalRow(t("LineItem Discount(-)"), formatCents(totals.lineDiscount))}
        {totalRow(t("Work Not Doing"), formatCents(totals.workNotDoing))}
        {totalRow(t("Office Discount(-)"), formatCents(totals.officeDiscount), [
          estimate.office_discount_note ?? "",
        ])}
        {totalRow(
          `<b>${t("Grand Totals")}</b>`,
          `<b>${formatCents(totals.grandTotal)}</b>`,
          [
            `${formatCents(totals.grossProfit)}`,
            <span className="pctpill" key="g">
              {formatPercent(totals.margin)}
            </span>,
          ],
          "strong",
        )}
        {unsignedWarning ? <p className="cowarn">{unsignedWarning}</p> : null}
        {totalRow(t("Material Cost"), formatCents(totals.materialCost), [
          tn(materialOrderCount, "1 order", "{n} orders"),
        ])}
        {totalRow(t("Labor Cost"), formatCents(totals.laborCost), [
          tn(workOrderCount, "1 order", "{n} orders"),
        ])}
        {totalRow(t("Other Expenses"), formatCents(totals.otherExpenses), [
          tn(expenseCount, "1 item", "{n} items"),
        ])}
        {totals.returns > 0 &&
          totalRow(
            t("Returns"),
            `<span class="credit">(${formatCents(totals.returns)})</span>`,
            ["", t("material sent back")],
          )}
        {additions.total > 0 &&
          totalRow(
            t("Added After Scheduling"),
            `<span class="overrun">${formatCents(additions.total)}</span>`,
            [
              "",
              <span
                className="pctpill overrun editable"
                key="add"
                title={t(
                  "Click to see what was added after the job went on the calendar",
                )}
                onClick={onShowAdditions}
              >
                {tn(additions.rows.length, "1 item", "{n} items")}
              </span>,
            ],
          )}
        {totalRow(
          `<b>${t("Total Cost")}</b>`,
          `<b>${formatCents(totals.totalCost)}</b>`,
          undefined,
          "sub",
        )}
        {totalRow(
          `<b>${t("Gross Profit")}</b>`,
          `<b>${formatCents(totals.grossProfit)}</b>`,
          [
            "",
            /* A button, not a pill of text with a `title` on it. The tooltip
               was the only thing that said this was a control and a phone
               never shows one; the tap target was 40x18. Jonathon,
               2026-09-15: "often times sam tries to adjust it but it won't let
               him." */
            <button
              className="pctpill editable"
              key="gp"
              title={t("Set the margin and reprice the job")}
              onClick={onEditMargin}
            >
              {formatPercent(totals.margin)}
              <span className="pen" aria-hidden="true">
                {" "}
                ✎
              </span>
              <span className="sr-only"> — {t("set the margin")}</span>
            </button>,
          ],
          // `ctl`: this row's last column is a control, so a phone keeps it.
          "sub ctl",
        )}
        {totalRow(t("Job Overhead"), formatCents(totals.jobOverhead), [
          `${Number(estimate.job_overhead_pct ?? 0)}%`,
        ])}
        {totalRow(
          `<b>${t("AGP")}</b>`,
          `<b>${formatCents(totals.agp)}</b>`,
          [
            "",
            <span className="pctpill" key="agp">
              {formatPercent(totals.agpMargin)}
            </span>,
          ],
          "sub",
        )}
      </div>
    </div>
  );
}

function ChangeOrders({
  estimate,
  job,
  rows,
  documents,
  products,
  tradeNames,
  onReload,
  onGuard,
  onPresent,
}: {
  estimate: Estimate;
  job: Job;
  rows: NonNullable<
    Awaited<ReturnType<typeof fetchEstimateChildren>>
  >["changeOrders"];
  /** What this estimate has sent out, newest first. */
  documents: ChangeOrderDocumentRecord[];
  products: Product[];
  tradeNames: string[];
  onReload: () => Promise<void>;
  onGuard: (action: () => Promise<void>) => Promise<void>;
  onPresent: () => void;
}) {
  const { profileId } = useSession();
  const total = rows.reduce((sum, r) => sum + toCents(r.amount), 0);
  const byId = new Map(documents.map((doc) => [doc.id, doc]));
  // The live one, if there is one: what the Withdraw button acts on, and what
  // makes Send read "Send again".
  const live = documents.find(
    (doc) => doc.status === "sent" || doc.status === "viewed",
  );
  const unsigned = rows.filter((row) => !row.signed_at).length;

  return (
    <div className="block esec sec-change">
      <h2>{t("Change Orders")}</h2>
      <div className="cohead">
        <button
          type="button"
          className="addrow"
          disabled={unsigned === 0}
          title={
            unsigned === 0
              ? t("Every change order here has been signed")
              : undefined
          }
          onClick={onPresent}
        >
          {live ? t("Send again") : t("Send to customer")}
        </button>
        {live ? (
          <button
            type="button"
            className="addrow"
            onClick={() =>
              void onGuard(async () => {
                await voidChangeOrderDocument(live.id);
                await onReload();
              })
            }
          >
            {t("Withdraw change order {number}", { number: live.number })}
          </button>
        ) : null}
      </div>
      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Trade")}</th>
              <th>{t("Product")}</th>
              <th>{t("Description")}</th>
              <th className="num">{t("Qty")}</th>
              <th className="num">{t("Unit Price")}</th>
              <th>{t("Unit")}</th>
              <th className="num">{t("Amount")}</th>
              <th>{t("Status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="tnone">
                  {t("No change orders.")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <select
                      style={{ minWidth: 150 }}
                      value={row.trade ?? ""}
                      onChange={(e) =>
                        void onGuard(async () => {
                          await updateRow("change_orders", row.id, {
                            trade: e.target.value || null,
                          });
                          await onReload();
                        })
                      }
                    >
                      <option value="">—</option>
                      {tradeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <ProductSelect
                      products={products}
                      grow={false}
                      value={row.product_id}
                      onChange={(product) =>
                        void onGuard(async () => {
                          const patch: Record<string, unknown> = {
                            product_id: product?.id ?? null,
                          };
                          if (product) {
                            // A change order is signed by the customer, so it
                            // reads the way the estimate does.
                            patch.description =
                              estimateLineDescription(product);
                            patch.unit = product.unit;
                            if (
                              toCents(row.unit_price) === 0 &&
                              product.default_price
                            ) {
                              patch.unit_price = product.default_price;
                            }
                          }
                          await updateRow("change_orders", row.id, patch);
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    {/* Free text, so an "Other" change order needs no product
                        in the catalogue to be written down. */}
                    <TextCell
                      value={row.description}
                      style={{ minWidth: 220 }}
                      onSave={(v) =>
                        void onGuard(async () => {
                          await updateRow("change_orders", row.id, {
                            description: v || null,
                          });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    <NumberCell
                      value={row.qty}
                      width={70}
                      variant="plain"
                      onSave={(qty) =>
                        void onGuard(async () => {
                          await updateRow("change_orders", row.id, { qty });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    <NumberCell
                      value={row.unit_price}
                      width={100}
                      onSave={(unit_price) =>
                        void onGuard(async () => {
                          await updateRow("change_orders", row.id, {
                            unit_price,
                          });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    <select
                      style={{ width: 80 }}
                      value={row.unit ?? ""}
                      onChange={(e) =>
                        void onGuard(async () => {
                          await updateRow("change_orders", row.id, {
                            unit: e.target.value || null,
                          });
                          await onReload();
                        })
                      }
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="num">{formatCents(toCents(row.amount))}</td>
                  <td>
                    <ChangeOrderStatus
                      row={row}
                      document={byId.get(row.document_id ?? "") ?? null}
                    />
                  </td>
                  <td>
                    {/* A signed line can be deleted — the database refuses
                        only an edit to its money or wording — but not
                        silently: the question says the customer signed it
                        (Jonathon, 2026-09-21, ADR 0181). The database then
                        records it as withdrawn beside the signed document,
                        whichever way the row goes, and the customer's copy
                        shows that. */}
                    <DeleteButton
                      table="change_orders"
                      id={row.id}
                      onDone={onReload}
                      onGuard={onGuard}
                      confirmMessage={t(changeOrderDeleteConfirmation(row))}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: "right", fontWeight: 600 }}>
                  {t("Total")}
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatCents(total as never)}
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <AddChangeOrder
        presets={products.filter((p) => p.change_order_preset)}
        onAdd={(preset) =>
          onGuard(async () => {
            await insertRow("change_orders", {
              job_id: job.id,
              estimate_id: estimate.id,
              trade: estimate.trade,
              product_id: preset?.id ?? null,
              description: preset?.name ?? null,
              qty: 1,
              unit: preset?.unit ?? "EA",
              // The preset carries what the customer pays, not what it costs.
              unit_price: preset?.default_price ?? 0,
              sort_order: nextSort(rows),
              created_by: profileId,
            });
            await onReload();
          })
        }
      />
    </div>
  );
}

/**
 * Where one change order has got to with the customer.
 *
 * The four words come from `changeOrderState` in packages/core rather than
 * being read off the row here, because "unsent" is not simply "no document" —
 * a withdrawn one counts as unsent too, and that rule belongs where it can be
 * tested.
 */
function ChangeOrderStatus({
  row,
  document,
}: {
  row: ChangeOrder;
  document: ChangeOrderDocumentRecord | null;
}) {
  const state = changeOrderState(row, document);
  if (state === "signed") {
    const label = t("Signed {date}", { date: fmtDate(row.signed_at ?? null) });
    // The customer's own page is the signed copy — the frozen snapshot with
    // their signature on it — so reading it back is the same link they signed,
    // not a second rendering of the same document to keep honest.
    return document ? (
      <a
        className="costat signed"
        href={`/c/${document.token}`}
        target="_blank"
        rel="noreferrer"
        title={t("Open the copy the customer signed")}
      >
        {label}
      </a>
    ) : (
      <span className="costat signed">{label}</span>
    );
  }
  if (state === "viewed")
    return <span className="costat sent">{t("Opened")}</span>;
  if (state === "sent") return <span className="costat sent">{t("Sent")}</span>;
  return <span className="costat unsent">{t("Not sent")}</span>;
}

/**
 * Adding a change order.
 *
 * Four extras come up on nearly every roof, always at the same price, and
 * typing them out again each time is how a $50 board becomes a $5 board. They
 * are picked from a list; "Other" opens an empty row to type into.
 *
 * The list is the product catalogue filtered to the ones marked as presets, so
 * the day plywood stops being $85 a sheet it changes in Tools like every other
 * price and no one has to touch this screen.
 */
function AddChangeOrder({
  presets,
  onAdd,
}: {
  presets: Product[];
  onAdd: (preset: Product | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button className="addrow" onClick={() => setOpen(true)}>
        {t("+ Add Change Order")}
      </button>
    );
  }

  return (
    <div className="copick">
      <select
        autoFocus
        defaultValue=""
        onChange={(e) => {
          const value = e.target.value;
          if (!value) return;
          setOpen(false);
          void onAdd(presets.find((p) => p.id === value) ?? null);
        }}
      >
        <option value="">{t("Choose a common item…")}</option>
        {presets.map((p) => (
          <option key={p.id} value={p.id}>
            {t("{name} — ${price} per {unit}", {
              name: p.name,
              price: Number(p.default_price ?? 0),
              unit: (p.unit ?? "EA").toLowerCase(),
            })}
          </option>
        ))}
        <option value="other">{t("Other — type it in")}</option>
      </select>
      <button className="btn-small" onClick={() => setOpen(false)}>
        {t("Cancel")}
      </button>
    </div>
  );
}

/**
 * Reading a credit memo instead of typing it.
 *
 * The parser is deliberately not trusted — see `parseCreditMemo`. What it finds
 * is written as ordinary return rows that can be corrected or deleted like any
 * other, and the person is told what was read and what the memo said it should
 * add up to, so a mismatch is visible rather than silent.
 *
 * A PDF's own text is tried first, because it is free and instant. When that
 * finds nothing — a scan, a photo, or columns that come out of the PDF out of
 * order — the file goes to the same reader a supplier's receipt goes to.
 * Jonathon, 2026-09-30: "the upload return document didn't work"; the text
 * alone was all it ever tried, and it only accepted a PDF (ADR 0244).
 *
 * The memo can also be picked from the job's CompanyCam photos, for a phone
 * with no CompanyCam add-on in its file picker (ADR 0261).
 */
function CreditMemoUpload({
  jobId,
  onRead,
}: {
  jobId: string;
  onRead: (memo: CreditMemo, filename: string) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  async function handle(file: File) {
    setBusy(true);
    try {
      const memo = await readReturnDocument(file);
      if (memo.lines.length === 0) {
        window.alert(
          t("Nothing could be read from {file}.", { file: file.name }) +
            "\n\n" +
            t("Add the lines by hand."),
        );
        return;
      }

      const read = memo.lines.reduce((sum, l) => sum + l.amount, 0);
      const expected = memo.subtotal ?? memo.total ?? null;
      const mismatch =
        expected !== null && Math.abs(read - expected) > 0.02
          ? "\n\n" +
            t(
              "Note: the lines add up to ${read} but the memo says ${expected}. Check for a line that could not be read.",
              {
                read: read.toFixed(2),
                expected: expected.toFixed(2),
              },
            )
          : "";

      const ok = window.confirm(
        t("Read {n} line(s) from {file}", {
          n: memo.lines.length,
          file: file.name,
        }) +
          (memo.creditNumber
            ? " " + t("(credit {number})", { number: memo.creditNumber })
            : "") +
          `:\n\n` +
          memo.lines
            .map(
              (l) => `• ${l.qty} x ${l.description} — $${l.amount.toFixed(2)}`,
            )
            .join("\n") +
          mismatch +
          "\n\n" +
          t("Add these as returns?"),
      );
      if (ok) await onRead(memo, file.name);
    } catch (cause) {
      // The reader says what went wrong — busy, not configured, too long —
      // and that is worth more than a button that quietly stops spinning.
      window.alert(
        t("{file} could not be read: {reason}", {
          file: file.name,
          reason: cause instanceof Error ? cause.message : String(cause),
        }) +
          "\n\n" +
          t("Add the lines by hand."),
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handle(file);
        }}
      />
      <button
        className="btn-small"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? t("Reading…") : t("Upload Credit Memo")}
      </button>
      <button
        className="btn-small"
        disabled={busy}
        onClick={() => setPicking(true)}
      >
        {t("Credit Memo From CompanyCam")}
      </button>
      {picking && (
        <CompanyCamPhotoPicker
          jobId={jobId}
          what="credit memo"
          onPick={(file) => void handle(file)}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

/** The PDF's own text first, then the reader. See `CreditMemoUpload`. */
async function readReturnDocument(file: File): Promise<CreditMemo> {
  const isPdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name ?? "");
  if (isPdf) {
    // Loaded on demand: pdf.js pulls in a web worker, which has no business
    // being started on every estimate screen.
    const { extractPdfText } = await import("../../lib/roofPdf");
    const fromText = parseCreditMemo(await extractPdfText(file));
    if (fromText.lines.length > 0) return fromText;
  }
  const { readSupplierQuote } = await import("../../lib/supplierQuotes");
  return creditMemoFromRead(await readSupplierQuote(file));
}

/**
 * Material sent back to the supplier.
 *
 * Amounts are typed as positive numbers because that is what the credit note
 * says. The ladder subtracts them, which is why they show in green and improve
 * the margin — asking a person to type a negative is asking for the day
 * somebody types two and the job looks $900 better than it is.
 */
function Returns({
  job,
  estimate,
  rows,
  products,
  suppliers,
  total,
  onReload,
  onGuard,
}: {
  job: Job;
  estimate: Estimate;
  rows: MaterialReturn[];
  products: Product[];
  suppliers: Supplier[];
  total: Cents;
  onReload: () => Promise<void>;
  onGuard: (action: () => Promise<void>) => Promise<void>;
}) {
  const { profileId } = useSession();

  return (
    <div className="block esec sec-return">
      <h2>{t("Returns")}</h2>
      <div className="na-hint" style={{ marginBottom: 8 }}>
        {t(
          "Material that went back. Enter what the credit is worth as a positive amount — it comes off the job cost.",
        )}
      </div>
      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Product")}</th>
              <th>{t("Description")}</th>
              <th>{t("Supplier")}</th>
              <th className="num">{t("Qty")}</th>
              <th className="num">{t("Unit Price")}</th>
              <th className="num">{t("Tax %")}</th>
              <th>{t("Credit Note #")}</th>
              <th className="num">{t("Credit")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="tnone">
                  {t("Nothing returned.")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {/* Jonathon, 2026-09-30: "we need to be able to manually
                        enter items not just select products", and a typed
                        name keeps when the box is clicked off rather than
                        only when its line in the list is clicked. */}
                    <ProductSelect
                      products={products}
                      grow={false}
                      value={row.product_id}
                      text={row.product_id ? null : row.description}
                      onText={(typed) =>
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            product_id: null,
                            description: typed,
                          });
                          await onReload();
                        })
                      }
                      onChange={(product) =>
                        void onGuard(async () => {
                          const patch: Record<string, unknown> = {
                            product_id: product?.id ?? null,
                          };
                          if (product) {
                            patch.description = [product.category, product.name]
                              .filter(Boolean)
                              .join(" | ");
                            patch.unit = product.unit;
                            // What we paid, not what we charged: a return is a
                            // credit against our own cost.
                            if (
                              toCents(row.unit_price) === 0 &&
                              product.default_cost
                            ) {
                              patch.unit_price = product.default_cost;
                            }
                          }
                          await updateRow("material_returns", row.id, patch);
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    <TextCell
                      value={row.description}
                      style={{ minWidth: 180 }}
                      onSave={(v) =>
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            description: v || null,
                          });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td>
                    <select
                      style={{ minWidth: 150 }}
                      value={row.supplier_id ?? ""}
                      onChange={(e) =>
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            supplier_id: e.target.value || null,
                          });
                          await onReload();
                        })
                      }
                    >
                      <option value="">—</option>
                      {suppliers.map((sup) => (
                        <option key={sup.id} value={sup.id}>
                          {sup.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <NumberCell
                      value={row.qty}
                      width={70}
                      variant="plain"
                      onSave={(qty) => {
                        // A new quantity, price or rate is a decision to work
                        // the credit out again (ADR 0244). Tabbing through
                        // the box is not, or it would wipe a typed credit.
                        if (qty === (Number(row.qty ?? 0) || 0)) return;
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            qty,
                            credit_amount: null,
                          });
                          await onReload();
                        });
                      }}
                    />
                  </td>
                  <td>
                    <NumberCell
                      value={row.unit_price}
                      width={100}
                      onSave={(unit_price) => {
                        if (toCents(unit_price) === toCents(row.unit_price))
                          return;
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            unit_price,
                            credit_amount: null,
                          });
                          await onReload();
                        });
                      }}
                    />
                  </td>
                  <td>
                    {/* The supplier credits the tax back with the material, so
                        it is part of what comes off the job. */}
                    <NumberCell
                      value={row.tax_rate}
                      width={70}
                      variant="plain"
                      onSave={(tax_rate) => {
                        if (tax_rate === (Number(row.tax_rate ?? 0) || 0))
                          return;
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            tax_rate,
                            credit_amount: null,
                          });
                          await onReload();
                        });
                      }}
                    />
                  </td>
                  <td>
                    <TextCell
                      value={row.credit_note}
                      style={{ minWidth: 120 }}
                      onSave={(v) =>
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            credit_note: v || null,
                          });
                          await onReload();
                        })
                      }
                    />
                  </td>
                  <td className="num credit">
                    {/* Typed off the memo, so the row matches it to the
                        cent. Clicking in and out without a change saves
                        nothing, or every credit would freeze as typed. */}
                    <NumberCell
                      value={toDecimal(returnCredit(row))}
                      width={100}
                      onSave={(credit) => {
                        if (
                          !returnCreditIsTyped(row) &&
                          toCents(credit) === returnCredit(row)
                        )
                          return;
                        void onGuard(async () => {
                          await updateRow("material_returns", row.id, {
                            credit_amount: credit,
                          });
                          await onReload();
                        });
                      }}
                    />
                    {returnCreditIsTyped(row) && (
                      <div className="na-hint">
                        {t("typed")} ·{" "}
                        <span
                          className="act"
                          role="button"
                          onClick={() =>
                            void onGuard(async () => {
                              await updateRow("material_returns", row.id, {
                                credit_amount: null,
                              });
                              await onReload();
                            })
                          }
                        >
                          {t("work it out")}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <DeleteButton
                      table="material_returns"
                      id={row.id}
                      onDone={onReload}
                      onGuard={onGuard}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={7} style={{ textAlign: "right", fontWeight: 600 }}>
                  {t("Total credit")}
                </td>
                <td className="num credit" style={{ fontWeight: 700 }}>
                  ({formatCents(total)})
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="rowacts" style={{ marginTop: 10 }}>
        <button
          className="addrow"
          onClick={() =>
            void onGuard(async () => {
              await insertRow("material_returns", {
                job_id: job.id,
                estimate_id: estimate.id,
                qty: 1,
                unit: "EA",
                unit_price: 0,
                sort_order: nextSort(rows),
                created_by: profileId,
              });
              await onReload();
            })
          }
        >
          {t("+ Add Return")}
        </button>
        <CreditMemoUpload
          jobId={job.id}
          onRead={async (memo, filename) => {
            await onGuard(async () => {
              let order = nextSort(rows);
              for (const line of memo.lines) {
                await insertRow("material_returns", {
                  job_id: job.id,
                  estimate_id: estimate.id,
                  description: line.description,
                  qty: line.qty,
                  unit: "EA",
                  unit_price: line.unitPrice,
                  // The memo's own rate when it broke tax out; otherwise the
                  // rate materials are billed at here.
                  tax_rate: memo.taxRate ?? DEFAULT_MATERIAL_TAX_RATE,
                  credit_note: memo.creditNumber,
                  document_filename: filename,
                  sort_order: (order += 10),
                  created_by: profileId,
                });
              }
              await onReload();
            });
          }}
        />
      </div>
    </div>
  );
}

function Expenses({
  job,
  estimate,
  rows,
  tradeNames,
  total,
  autoExpenses,
  insuranceLines,
  crews,
  crewWorkOrders,
  layouts,
  onReload,
  onGuard,
  onCopyFromEstimate,
  onSaveEstimate,
  onOverridesChanged,
}: {
  job: Job;
  estimate: Estimate;
  rows: NonNullable<
    Awaited<ReturnType<typeof fetchEstimateChildren>>
  >["expenses"];
  tradeNames: string[];
  total: number;
  autoExpenses: LadderTotals["autoExpenses"];
  /** Insurance budgeted by hand for subs with no work order on this estimate. */
  insuranceLines: readonly EstimateInsuranceLine[];
  /** Active crews, for the sub picker on those lines. */
  crews: readonly Crew[];
  /**
   * The crew on each work order here — only to say which hand-added line an
   * order covers, whatever the order totals (ADR 0169, amending ADR 0146).
   */
  crewWorkOrders: readonly { crewId: string | null }[];
  /** The job's roofs — only to show what the crane's days were worked out from. */
  layouts: readonly { squares?: unknown; waste_pct?: unknown }[];
  onReload: () => Promise<void>;
  onGuard: (action: () => Promise<void>) => Promise<void>;
  /** Opens the copy picker. The modal itself is owned by the card. */
  onCopyFromEstimate: () => void;
  onSaveEstimate: (patch: Partial<Estimate>) => Promise<void>;
  /** Reloads the card's copy of the overrides, so the ladder follows the row. */
  onOverridesChanged: () => Promise<void> | void;
}) {
  const { profileId } = useSession();
  const [editing, setEditing] = useState<JobExpense | null>(null);
  const receipts = useExpenseReceipts(
    job.id,
    rows.map((r) => r.id),
    profileId,
  );

  // How an automatic expense was paid. The amount is worked out from the
  // contract and nobody types it; how it left the bank is a separate fact and
  // somebody does know it. Kept beside the estimate rather than on the row,
  // because there is no row — the expense is a calculation.
  const paidBy = useAsync(
    async () =>
      unwrap<
        {
          expense_key: string;
          payment_type: string | null;
          trade: string | null;
          is_change_order: boolean | null;
          amount_override: number | null;
        }[]
      >(
        await supabase
          .from("auto_expense_payments")
          .select(
            "expense_key,payment_type,trade,is_change_order,amount_override",
          )
          .eq("estimate_id", estimate.id),
      ),
    [estimate.id],
  );
  const detailOf = (key: string) =>
    (paidBy.data ?? []).find((r) => r.expense_key === key);
  const paymentOf = (key: string) => detailOf(key)?.payment_type ?? "";
  // Null means "this estimate's trade", not "no trade" — so it follows the
  // template trump rather than freezing a copy. See the migration.
  //
  // Which is why this returns the empty string for a row nobody has chosen a
  // trade on, rather than filling in the estimate's: the select's blank option
  // is "the estimate's trade", a real answer with its own label, and resolving
  // it here would make choosing the estimate's current trade on purpose
  // indistinguishable from leaving it to follow.
  const chosenTradeOf = (key: string) => detailOf(key)?.trade ?? "";
  const isChangeOrder = (key: string) =>
    detailOf(key)?.is_change_order ?? false;
  const overrideOf = (key: string) => detailOf(key)?.amount_override ?? null;

  /** One upsert for every editable fact about a calculated expense. */
  const setAutoDetail = (key: string, patch: Record<string, unknown>) =>
    onGuard(async () => {
      await supabase
        .from("auto_expense_payments")
        .upsert(
          {
            estimate_id: estimate.id,
            expense_key: key,
            ...patch,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "estimate_id,expense_key" },
        )
        .throwOnError();
      await paidBy.reload();
      // The amount is the one field the ladder reads, so the card has to hear
      // about it. Reloading unconditionally keeps this one call site honest —
      // there is no branch to forget when a new editable field arrives.
      await onOverridesChanged();
    });

  /**
   * A line is edited and deleted on its row in the table — see
   * `InsuranceRowControls`. The amount is never written: it is worked out on
   * read from the crew's certificates and what they are paid (ADR 0198).
   */
  const saveInsurance = (id: string, patch: Record<string, unknown>) =>
    onGuard(async () => {
      await updateRow("estimate_insurance_lines", id, {
        ...patch,
        updated_at: new Date().toISOString(),
      });
      await onReload();
    });

  const deleteInsurance = (id: string) =>
    onGuard(async () => {
      await deleteRow("estimate_insurance_lines", id);
      await onReload();
    });

  // The same match `computeAutoExpenses` uses to stop charging the line, so
  // what the row says and what is charged can never disagree.
  const coveredCrewIds = crewsWithBoth(
    crewWorkOrders,
    insuranceLines.map((l) => l.crew_id),
  );

  // A crew appears once per estimate — the labour goes up, the line does not
  // multiply — so one already budgeted is off the list rather than offered and
  // then refused by the database.
  const budgeted = new Set(insuranceLines.map((l) => l.crew_id));
  const availableCrews = crews.filter((c) => !budgeted.has(c.id));

  return (
    <div className="block esec sec-expense">
      <h2>{t("Other Expenses")}</h2>
      <CraneOption
        jobType={job.job_type}
        estimate={estimate}
        layouts={layouts}
        onSave={onSaveEstimate}
      />
      {/* Undoable, like the two order editors. Deleting a row here no longer
          stops to ask — see lib/editHistory.tsx. */}
      <EditHistoryProvider onChanged={onReload}>
        <UndoRedoButtons />
        {editing && (
          <ExpenseModal
            expense={editing}
            trades={tradeNames}
            onClose={() => setEditing(null)}
            onSave={(patch) =>
              onGuard(async () => {
                await updateRow("job_expenses", editing.id, patch);
                await onReload();
              })
            }
          />
        )}
        <ExpenseRows
          rows={rows}
          tradeNames={tradeNames}
          total={total}
          autoExpenses={autoExpenses}
          estimateTrade={estimate.trade ?? null}
          insuranceLines={insuranceLines}
          crews={crews}
          coveredCrewIds={coveredCrewIds}
          onSaveInsurance={saveInsurance}
          onDeleteInsurance={deleteInsurance}
          paymentOf={paymentOf}
          chosenTradeOf={chosenTradeOf}
          isAutoChangeOrder={isChangeOrder}
          overrideOf={overrideOf}
          setAutoDetail={setAutoDetail}
          onEdit={setEditing}
          receiptsOf={(id) => receipts.byExpense.get(id) ?? []}
          // Not through onGuard: a failed upload is said beside the button
          // that was pressed, not at the top of the card.
          onUploadReceipt={receipts.upload}
        />
        <AddExpenseLine
          availableCrews={availableCrews}
          onAddExpense={() =>
            void onGuard(async () => {
              const created = await insertRow<JobExpense>("job_expenses", {
                job_id: job.id,
                estimate_id: estimate.id,
                name: "New expense",
                amount: 0,
                payment_type: "Cash",
                trade: estimate.trade,
                sort_order: nextSort(rows),
                created_by: profileId,
              });
              await onReload();
              setEditing(created);
            })
          }
          onAddInsurance={(crewId) =>
            void onGuard(async () => {
              await insertRow("estimate_insurance_lines", {
                estimate_id: estimate.id,
                crew_id: crewId,
                labor_amount: 0,
                created_by: profileId,
              });
              await onReload();
            })
          }
        />
        {/* Not "from measurements" — an expense is not measured off a roof.
            The permit, the general liability and the warranty are worked out
            from the estimate's own revenue and the rest are typed. Jonathon:
            "the other expenses can still operate as normal." Copying them off
            another estimate is still worth a button. */}
        <button
          className="addrow ghost"
          style={{ marginLeft: 8 }}
          onClick={onCopyFromEstimate}
        >
          {t("Copy From Another Estimate")}
        </button>
      </EditHistoryProvider>
    </div>
  );
}

/**
 * The expenses themselves — typed rows and calculated ones, in one table.
 *
 * Every column a person is allowed to change is changed **here**, in the row,
 * on one horizontal line. Jonathon, 2026-08-25: "I also wasn't able to edit the
 * other expenses. I need to be able to fully edit them." Before this, the row
 * was a link and everything but the change-order flag lived behind a modal, so
 * correcting a tracking number meant opening a form, and correcting six meant
 * opening six.
 *
 * The modal is still there behind Edit, for the one field a table cell cannot
 * hold: the note is a paragraph explaining how a figure was arrived at, and the
 * cell here is the one-line version of it.
 *
 * Its own component because `useTrackedRows` has to run inside the
 * `EditHistoryProvider` above — every write goes through it, so undo knows.
 */
/**
 * The amount box on a calculated expense.
 *
 * Local state while it is being typed in, saved on blur or Enter, because
 * saving per keystroke would re-run the ladder — and the ladder feeds the GC
 * fee, which feeds the contract, which feeds the GC fee again.
 *
 * Shows dollars and takes dollars; everything below it is cents. Empty means
 * "no override", which is how the calculation is put back — so the box has to
 * distinguish an empty string from a zero, and 0 is a real answer a person is
 * allowed to give.
 */
export function AutoExpenseAmount({
  amount,
  override,
  removed,
  onSave,
}: {
  amount: Cents;
  override: number | null;
  /**
   * Struck off the job. There is then no amount to correct — only the figure
   * the rule would have charged, kept legible for whoever asks later why this
   * job carried none. Shown rather than hidden: the row is the only place that
   * number still exists.
   */
  removed?: boolean | undefined;
  onSave: (amount: number | null) => Promise<void>;
}) {
  const asDollars = (value: number | null) =>
    value == null ? "" : String(value / 100);
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? asDollars(override ?? amount);

  const commit = async () => {
    if (draft === null) return;
    const trimmed = draft.trim();
    setDraft(null);
    // Blank puts the calculation back. Anything unreadable is a typo, not an
    // instruction, so it is dropped rather than saved as zero.
    if (trimmed === "") {
      if (override != null) await onSave(null);
      return;
    }
    const dollars = Number(trimmed);
    if (!Number.isFinite(dollars) || dollars < 0) return;
    const asCents = Math.round(dollars * 100);
    // Typing the calculated figure back in is not an override — it is
    // agreement. Saving it would freeze today's number against tomorrow's
    // contract.
    if (override == null && asCents === amount) return;
    await onSave(asCents);
  };

  if (removed) return <>{formatCents(amount)}</>;

  return (
    <input
      className="num"
      inputMode="decimal"
      value={shown}
      title={
        override == null
          ? t(
              "Worked out from the estimate. Type over it to correct this job; clear it to go back.",
            )
          : t("Edited. Clear the box to go back to the calculated amount.")
      }
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setDraft(null);
      }}
    />
  );
}

/**
 * Exported for `insurance-warning.test.tsx`, which is the only way to see the
 * warning without standing up the whole estimate card.
 */
export function ExpenseRows({
  rows,
  tradeNames,
  total,
  autoExpenses,
  estimateTrade,
  insuranceLines = [],
  crews = [],
  coveredCrewIds,
  onSaveInsurance,
  onDeleteInsurance,
  paymentOf,
  chosenTradeOf,
  isAutoChangeOrder,
  overrideOf,
  setAutoDetail,
  onEdit,
  receiptsOf,
  onUploadReceipt,
}: {
  rows: NonNullable<
    Awaited<ReturnType<typeof fetchEstimateChildren>>
  >["expenses"];
  tradeNames: string[];
  total: number;
  autoExpenses: LadderTotals["autoExpenses"];
  estimateTrade: string | null;
  /**
   * Insurance budgeted by hand. Defaulted rather than required: the warning
   * test stands this table up on its own, and "no insurance budgeted by hand"
   * is the right reading of a table that was not given any.
   */
  insuranceLines?: readonly EstimateInsuranceLine[];
  crews?: readonly Crew[];
  coveredCrewIds?: ReadonlySet<string>;
  onSaveInsurance?: (
    id: string,
    patch: Record<string, unknown>,
  ) => Promise<void>;
  onDeleteInsurance?: (id: string) => Promise<void>;
  paymentOf: (key: string) => string;
  chosenTradeOf: (key: string) => string;
  isAutoChangeOrder: (key: string) => boolean;
  overrideOf: (key: string) => number | null;
  setAutoDetail: (key: string, patch: Record<string, unknown>) => Promise<void>;
  onEdit: (row: JobExpense) => void;
  /**
   * The receipts uploaded on a typed expense, and the upload. Optional, like
   * the insurance props: a table stood up without them has no receipt buttons.
   */
  receiptsOf?: (expenseId: string) => readonly JobFileEntry[];
  onUploadReceipt?: (
    row: { id: string; name: string | null },
    file: File,
  ) => Promise<void>;
}) {
  const tracked = useTrackedRows("job_expenses");
  const patch = (
    row: (typeof rows)[number],
    values: Record<string, unknown>,
    label: string,
  ) => void tracked.patch(row as unknown as TrackedRow, values, label);

  /** The codes on offer, plus whichever retired one this row already carries. */
  const paymentOptions = (current: string | null | undefined) =>
    current && !PAYMENT_TYPES.includes(current as never)
      ? [current, ...PAYMENT_TYPES]
      : PAYMENT_TYPES;

  /**
   * The trades on offer, plus whichever retired one this row already carries.
   *
   * Reference values are never renamed (rule 8), so an old trade is retired
   * rather than gone — and a select whose value is not in its list renders
   * blank, which would read as "no trade" on a row that holds one.
   */
  const tradeOptions = (current: string | null | undefined) => [
    ...new Set([...tradeNames, ...(current ? [current] : [])]),
  ];

  return (
    <>
      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Name")}</th>
              <th className="num">{t("Amount")}</th>
              {/* Sized to "Credit/Debit", the longest value it ever holds. */}
              <th className="paycol">{t("Payment Type")}</th>
              <th>{t("Trade")}</th>
              <th>{t("Tracking #")}</th>
              <th>{t("Note")}</th>
              <th>{t("Change Order")}</th>
              <th>{t("Date")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* The amount is worked out from the contract, and until
              2026-08-31 that made it the one column nobody could touch.
              Jonathon: "we always need to be able to edit other expenses." So
              the box holds the calculated figure and a person can type over
              it; the basis beside the name then says what the rule would have
              produced, which is the whole reason an edit here is safe. Blank
              it and the calculation comes back. */}
            {autoExpenses.map((row) => {
              // The line a person added by hand, when this row is one. Its
              // sub, labour and trade are edited here, on the row, rather than
              // in a control of their own somewhere else (ADR 0198). Only the
              // liability row carries them: the comp row beside it is the same
              // labour rated by the second policy.
              const line =
                row.insuranceLineId && row.key.startsWith("general_liability")
                  ? insuranceLines.find((l) => l.id === row.insuranceLineId)
                  : undefined;
              return (
                <tr
                  key={row.key}
                  className={`autorow${row.removed ? " gone" : ""}`}
                >
                  <td
                    title={`${t(row.name)} — ${row.basis}${
                      row.warning ? ` — ${row.warning}` : ""
                    }`}
                  >
                    <div className="rowline">
                      <span className="an">{t(row.name)}</span>
                      <span className="na-hint">
                        {row.removed
                          ? t("removed — {basis}", { basis: row.basis })
                          : row.basis}
                      </span>
                      {line && onSaveInsurance && onDeleteInsurance ? (
                        <InsuranceRowControls
                          line={line}
                          crews={insuranceCrewChoices(
                            crews,
                            insuranceLines,
                            line,
                          )}
                          estimateTrade={estimateTrade}
                          tradeNames={tradeNames}
                          covered={coveredCrewIds?.has(line.crew_id) ?? false}
                          onSave={onSaveInsurance}
                          onDelete={onDeleteInsurance}
                        />
                      ) : null}
                      {/* A crew whose certificate is missing or out of date is
                        budgeted as carrying nothing. Saying so here is the
                        only place anybody would find out in time to go and
                        ask for the certificate — it is worth the difference
                        between 0.7% and 19.9% of the labour. A row struck off
                        carries no warning: nothing is charged. */}
                      {row.warning && !row.removed ? (
                        <span className="autowarn">{t(row.warning)}</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="num">
                    <AutoExpenseAmount
                      amount={row.amount}
                      override={overrideOf(row.key)}
                      removed={row.removed}
                      onSave={(amount) =>
                        setAutoDetail(row.key, { amount_override: amount })
                      }
                    />
                  </td>
                  <td>
                    {/* A struck-off expense was not paid, and did not go on a
                    trade. The boxes stay on screen so the row keeps its shape
                    in the table, but there is nothing to answer in them. */}
                    <select
                      value={paymentOf(row.key)}
                      disabled={row.removed}
                      onChange={(e) =>
                        void setAutoDetail(row.key, {
                          payment_type: e.target.value || null,
                        })
                      }
                    >
                      <option value="">—</option>
                      {PAYMENT_TYPES.map((name) => (
                        <option key={name} value={name}>
                          {t(PAYMENT_TYPE_LABEL[name] ?? name)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {/* An insurance line's trade chooses its rating class and
                      belongs to the line, not to this row — it is in the
                      controls beside the name. One trade box per row. */}
                    <select
                      value={line ? (line.trade ?? "") : chosenTradeOf(row.key)}
                      disabled={row.removed || !!line}
                      onChange={(e) =>
                        void setAutoDetail(row.key, {
                          trade: e.target.value || null,
                        })
                      }
                    >
                      {/* Empty still means "whatever this estimate is" in the
                      database — the row follows the estimate rather than
                      freezing a copy. On screen it just reads as the trade,
                      because that is what it is: Jonathon, 2026-08-08, "just
                      needs to say the actual trade". */}
                      <option value="">{estimateTrade ?? "—"}</option>
                      {tradeOptions(chosenTradeOf(row.key)).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </td>
                  {/* No tracking number on a calculated expense: nothing was
                  ordered, so there is nothing to track. And no note — there is
                  no row to hang one on. */}
                  <td className="sub" />
                  <td className="sub" />
                  <td>
                    <button
                      className={`tog${isAutoChangeOrder(row.key) ? " on" : ""}`}
                      disabled={row.removed}
                      // Deliberately not the wording used on a typed expense.
                      // There the flag keeps the row out of the additions report;
                      // here there is no row and no moment it was added, so it
                      // records what happened and nothing downstream reads it.
                      // Two controls that look the same and mean different things
                      // is worse than two that read differently.
                      title={
                        isAutoChangeOrder(row.key)
                          ? t(
                              "Recorded as agreed with the customer. A note — the amount is still worked out from the estimate.",
                            )
                          : t(
                              "Mark as agreed with the customer rather than something the estimate missed",
                            )
                      }
                      onClick={() =>
                        void setAutoDetail(row.key, {
                          is_change_order: !isAutoChangeOrder(row.key),
                        })
                      }
                    />
                  </td>
                  <td className="sub" />
                  <td className="rowacts">
                    {/* There is no row to delete — the expense is worked out from
                    the estimate — so ✕ records that this job does not have it
                    and ↺ puts it back. It has to be able to come back: a rule
                    the whole company shares cannot be retyped by hand on one
                    job once it is gone. Jonathon, 2026-09-01: "i want to be
                    able to delete the line items too. not just edit." */}
                    {/* A hand-added insurance line is a real row somebody
                      wrote, so it is deleted rather than struck off — and its
                      ✕ is with its own controls, beside the name. */}
                    {row.insuranceLineId ? null : row.removed ? (
                      <button
                        className="btn-small"
                        title={t("Put this expense back on the job")}
                        onClick={() =>
                          void setAutoDetail(row.key, { removed: false })
                        }
                      >
                        ↺ {t("Restore")}
                      </button>
                    ) : (
                      <button
                        className="xbtn"
                        title={t(
                          "This job does not have this expense. It stays listed, struck through, and can be restored.",
                        )}
                        onClick={() =>
                          void setAutoDetail(row.key, { removed: true })
                        }
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && autoExpenses.length === 0 ? (
              <tr>
                <td colSpan={9} className="tnone">
                  {t("No expenses.")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <TextCell
                      value={row.name}
                      placeholder={t("What this was")}
                      style={{ minWidth: 180 }}
                      onSave={(v) =>
                        patch(
                          row,
                          { name: v || null },
                          v
                            ? t("Renamed to “{name}”", { name: v })
                            : t("Cleared a name"),
                        )
                      }
                    />
                  </td>
                  <td className="num">
                    <NumberCell
                      value={row.amount}
                      width={110}
                      onSave={(amount) =>
                        patch(row, { amount }, t("Changed an amount"))
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={row.payment_type ?? ""}
                      onChange={(e) =>
                        patch(
                          row,
                          { payment_type: e.target.value || null },
                          t("Changed a payment type"),
                        )
                      }
                    >
                      <option value="">—</option>
                      {paymentOptions(row.payment_type).map((name) => (
                        <option key={name} value={name}>
                          {t(PAYMENT_TYPE_LABEL[name] ?? name)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.trade ?? ""}
                      onChange={(e) =>
                        patch(
                          row,
                          { trade: e.target.value || null },
                          t("Changed a trade"),
                        )
                      }
                    >
                      <option value="">—</option>
                      {tradeOptions(row.trade).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <TextCell
                      value={row.tracking_number}
                      placeholder={t("Invoice or receipt")}
                      style={{ minWidth: 130 }}
                      onSave={(v) =>
                        patch(
                          row,
                          { tracking_number: v || null },
                          t("Changed a tracking number"),
                        )
                      }
                    />
                  </td>
                  <td title={row.note ?? undefined}>
                    {/* The one-line version of the note. A paragraph is typed
                        behind Edit; this is for correcting a word without
                        opening anything. */}
                    <TextCell
                      value={row.note}
                      placeholder={t("Internal")}
                      style={{ minWidth: 160 }}
                      onSave={(v) =>
                        patch(row, { note: v || null }, t("Changed a note"))
                      }
                    />
                  </td>
                  <td>
                    {/* In place: it is one fact, and it is the one that decides
                      whether this lands in the added-after-scheduling figure. */}
                    <button
                      className={`tog${row.is_change_order ? " on" : ""}`}
                      title={
                        row.is_change_order
                          ? t("Agreed with the customer")
                          : t("Counts as something the estimate missed")
                      }
                      onClick={() =>
                        patch(
                          row,
                          { is_change_order: !row.is_change_order },
                          t("Changed a change-order flag"),
                        )
                      }
                    />
                  </td>
                  <td className="sub">{fmtDateTime(row.occurred_at)}</td>
                  <td className="rowacts">
                    <button
                      className="btn-small"
                      title={t("The note, with room to write it")}
                      onClick={() => onEdit(row as unknown as JobExpense)}
                    >
                      {t("Edit")}
                    </button>
                    {/* Kept here and on the job's Docs card, by one row. A
                        calculated expense has no row to hang one on. */}
                    {onUploadReceipt ? (
                      <ExpenseReceiptButtons
                        receipts={receiptsOf?.(row.id) ?? []}
                        onUpload={(file) =>
                          onUploadReceipt(
                            { id: row.id, name: row.name ?? null },
                            file,
                          )
                        }
                      />
                    ) : null}
                    <DeleteLineButton
                      table="job_expenses"
                      row={row as unknown as Record<string, unknown>}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {t("Total")}
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatCents(total as never)}
                </td>
                <td colSpan={7} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ cells ---

export function TextCell({
  value,
  onSave,
  placeholder,
  style,
  multiline = false,
}: {
  value: string | null | undefined;
  onSave: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  /**
   * For the boxes that hold a sentence rather than a word — a work order's
   * description is the whole job in a paragraph. It wraps, grows to fit and
   * drags; it saves on the way out, the same as the one-line box does.
   */
  multiline?: boolean;
}) {
  const [local, setLocal] = useState(value ?? "");
  const [dirty, setDirty] = useState(false);
  if (!dirty && (value ?? "") !== local) setLocal(value ?? "");

  const change = (typed: string) => {
    setLocal(typed);
    setDirty(true);
  };

  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    onSave(local);
  };

  if (multiline)
    return (
      <GrowBox
        rows={2}
        value={local}
        placeholder={placeholder}
        style={style}
        onChange={(e) => change(e.target.value)}
        onBlur={commit}
      />
    );

  return (
    <input
      value={local}
      placeholder={placeholder}
      style={style}
      onChange={(e) => change(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/**
 * A numeric input that shows a formatted value when idle and saves a plain
 * number. Editing shows the raw text so a partly-typed number is not
 * reformatted under the cursor.
 *
 * The variant matters. Money always reads to two decimals; a quantity or a
 * percentage must not, because "17.00%" and "1.00 SQ" are simply wrong — and
 * the prototype showed neither.
 *
 * An emptied box is a zero unless `onClear` is given, in which case it is
 * handed there instead of being saved: a cell whose value can be "no value"
 * (a typed tax going back to its rate, ADR 0243) must not read an empty box
 * as a typed $0.
 */
export function NumberCell({
  value,
  onSave,
  onClear,
  width,
  variant = "money",
}: {
  value: unknown;
  onSave: (value: number) => void;
  onClear?: () => void;
  width?: number;
  variant?: "money" | "plain";
}) {
  const asNumber = Number(value ?? 0) || 0;
  const display =
    variant === "money" ? formatCents(toCents(value)) : String(asNumber);

  const [local, setLocal] = useState<string | null>(null);

  return (
    <input
      className="num"
      style={width ? { width } : undefined}
      value={local ?? display}
      onChange={(e) => setLocal(e.target.value)}
      onFocus={() => setLocal(String(asNumber))}
      onBlur={() => {
        if (local === null) return;
        setLocal(null);
        if (onClear && local.trim() === "") {
          onClear();
          return;
        }
        if (variant === "money") {
          onSave(toDecimal(toCents(local)));
          return;
        }
        const parsed = Number.parseFloat(local.replace(/[$,\s]/g, ""));
        onSave(Number.isFinite(parsed) ? parsed : 0);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/**
 * Pick a product by typing — or type something the catalogue does not have.
 *
 * 650 products in a dropdown is a scroll, not a choice. This filters as you
 * type — every word has to match somewhere, so the list shrinks as the query
 * grows — and ranks the closest name first, then the most-ordered. When no
 * product matches every word, the ones most of the words matched are offered
 * under a "Close matches" line rather than nothing (ADR 0212). Matching lives
 * in @roof-hawk/core so it is tested; this is just the box.
 *
 * **The list fits the screen the keyboard leaves.** Samuel, 2026-09-24, on his
 * Android: he searched "nails", saw two framing nails and could not scroll to
 * the roofing nails. The list opened below the box and ran behind the
 * keyboard, and five rows were shorter than its cap, so it had nothing to
 * scroll. It now measures the visible part of the screen (`visibleArea`),
 * opens above the box when there is more room there, and is never taller than
 * that room — `placeDropdown`, which is where the arithmetic is tested.
 *
 * **Not everything a crew is paid for is a product.** Jonathon, 2026-08-19: "we
 * need to add work order line items and material line items just by typing in
 * them ... they don't have to be products." So where `onText` is given, what has
 * been typed is offered as the first line of the list and taking it writes the
 * name straight onto the row with no product behind it. The catalogue is still
 * there and still first-class; it is no longer the only way through.
 *
 * A row with a typed name shows it, rather than the empty search box a row with
 * no product used to show — which read as a line that had not saved.
 *
 * Where a product matches what was typed well — every typed word is in its
 * name — it is offered above the typed line, so Return on a phone keyboard
 * picks the nail rather than writing "roofing nails" with no product behind
 * it. Jonathon agreed to that on 2026-09-24; the typed line still comes first
 * when the catalogue has only a near-miss.
 */
export function ProductSelect({
  products,
  value,
  onChange,
  text,
  onText,
  onCustom,
  width = 200,
  grow = true,
}: {
  products: Product[];
  value: string | null | undefined;
  onChange: (product: Product | null) => void;
  /** What this row is called when no product is behind it. */
  text?: string | null;
  /** Given: typing a name that matches nothing is an answer, not a dead end. */
  onText?: (value: string) => void;
  /**
   * Given: a one-off repair is offered at the top of the list, before anything
   * has been typed. Unlike `onText` it is not a name — it is "none of these",
   * and the screen that offers it has its own boxes for the rest.
   */
  onCustom?: () => void;
  width?: number;
  /** False for a table row that must stay one line tall until dragged. */
  grow?: boolean;
}) {
  const selected = products.find((p) => p.id === value) ?? null;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  // Clicking anywhere else closes the list without changing the selection.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const found = useMemo(
    () =>
      open
        ? searchCatalogue(products, query, 40)
        : { hits: [], close: [], strong: false },
    [products, query, open],
  );
  const hits = found.hits;

  /**
   * The list as it is actually offered. Before anything is typed: the repair,
   * then the catalogue, because a rep opening this box for a one-off repair
   * has nothing to search for. Once something is typed: what was typed, then
   * the catalogue, then the close matches, then the repair. Typed above the
   * catalogue because somebody who has just typed "Haul the old skylight away"
   * is not looking for a near-miss product — unless the first product is a
   * strong match, every typed word in its name, in which case they were
   * looking for exactly that and it goes first. The repair drops to the
   * bottom because the highlight sits on the first entry and Enter takes it —
   * somebody who has typed "seamless" and pressed Enter wants the gutters,
   * not a blank repair line.
   */
  const entries = useMemo(() => {
    const typed = query.trim();
    const custom = onCustom ? [{ kind: "custom" as const }] : [];
    const text =
      onText && typed ? [{ kind: "text" as const, value: typed }] : [];
    const catalogue = hits.map((product) => ({
      kind: "product" as const,
      product,
    }));
    const close = found.close.map((product) => ({
      kind: "close" as const,
      product,
    }));
    if (!typed) return [...custom, ...text, ...catalogue];
    return found.strong
      ? [...catalogue, ...text, ...close, ...custom]
      : [...text, ...catalogue, ...close, ...custom];
  }, [found, hits, onCustom, onText, query]);

  // The line-item tables scroll horizontally, which clips anything absolutely
  // positioned inside them. The list is rendered to <body> at fixed
  // coordinates so it escapes that, and follows the input on scroll/resize —
  // including the visual viewport's own, which is how a phone says the
  // keyboard has opened or the page has been nudged to show the box.
  const [place, setPlace] = useState<ReturnType<typeof placeDropdown> | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const rect = box.current?.getBoundingClientRect();
      if (!rect) return setPlace(null);
      setPlace(
        placeDropdown(rect, visibleArea(), window.innerHeight, {
          minWidth: 300,
        }),
      );
    };
    measure();
    const vv = window.visualViewport;
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, [open]);

  const choose = (entry: (typeof entries)[number]) => {
    if (entry.kind === "custom") onCustom?.();
    else if (entry.kind === "text") onText?.(entry.value);
    else onChange(entry.product);
    // A close match is a product like any other once it is chosen.
    setQuery("");
    setOpen(false);
  };

  /**
   * Leaving the box keeps what was typed in it.
   *
   * It used to be thrown away. Everything typed here lived in `query` until
   * somebody picked a line out of the list below — a product, or the "use what
   * you typed" entry — and clicking anywhere else closed the list and lost it.
   * A crew chief's work step, typed and then clicked away from, simply was not
   * there. Jonathon, 2026-09-23: it "should auto save our option instead of
   * making us clicking on the line item to save it."
   *
   * Picking from the list still goes through `choose`: those entries cancel
   * the mouse-down that would blur this box, so a choice never arrives here as
   * a half-typed search as well.
   */
  const keepTyped = () => {
    const typed = query.trim();
    setQuery("");
    setOpen(false);
    if (!typed || !onText) return;
    // Typing a name back over itself is not an edit, and writing it would
    // clear the product behind a line that nobody changed.
    if (typed === (selected?.name ?? text ?? "")) return;
    onText(typed);
  };

  return (
    <div className="psel" style={{ minWidth: width }} ref={box}>
      {/* Order rows stay one line tall until dragged. Pickers outside a table
          keep growing so their full value remains visible. */}
      <GrowBox
        rows={1}
        grow={grow}
        title={open ? undefined : (selected?.name ?? text ?? undefined)}
        value={open ? query : (selected?.name ?? text ?? "")}
        placeholder={productPlaceholder(selected, Boolean(onText))}
        onFocus={() => {
          setQuery("");
          setActive(0);
          setOpen(true);
        }}
        onBlur={keepTyped}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // Escape cancels this search. Blur must not save its old query.
            setQuery("");
            setOpen(false);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, entries.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            // A line's name is never two paragraphs, so Enter picks rather
            // than types — and where there is nothing to pick it keeps what
            // was typed, the same as clicking away would.
            e.preventDefault();
            if (entries[active]) choose(entries[active]);
            else keepTyped();
          }
        }}
      />
      {/* No clear ✕ here.
          Jonathon, 2026-08-13: "there should only be one x on the right per
          line item where we can delete line items." This was the second one,
          and it did something different from the one beside it — it emptied the
          row rather than removing it, leaving a blank line that read as a
          delete which had not worked. To change the product, type a new one
          over it; to remove the line, use the ✕ at the end of the row. */}
      {open && place && (
        <ProductDropdown
          place={place}
          entries={entries}
          active={active}
          setActive={setActive}
          choose={choose}
          query={query}
          noMatch={hits.length + found.close.length === 0}
        />
      )}
    </div>
  );
}

type ProductOption =
  | { kind: "custom" }
  | { kind: "text"; value: string }
  | { kind: "product" | "close"; product: Product };

function productPlaceholder(selected: Product | null, canType: boolean) {
  if (selected) return selected.name;
  return canType ? t("Search or type a name…") : t("Search products…");
}

function ProductDropdown({
  place,
  entries,
  active,
  setActive,
  choose,
  query,
  noMatch,
}: {
  place: ReturnType<typeof placeDropdown>;
  entries: ProductOption[];
  active: number;
  setActive: (index: number) => void;
  choose: (entry: ProductOption) => void;
  query: string;
  noMatch: boolean;
}) {
  return createPortal(
    <div
      className="ac-drop psel-drop"
      style={{
        // Both said, always: the stylesheet gives `.ac-drop` a `top`
        // of its own, and an inline `bottom` on its own loses to it.
        top: place.top ?? "auto",
        bottom: place.bottom ?? "auto",
        left: place.left,
        width: place.width,
        maxHeight: place.maxHeight,
      }}
    >
      {noMatch && (
        <div className="ac-item">
          {t("No product matches “{query}”.", { query })}
        </div>
      )}
      {entries.map((entry, i) =>
        entry.kind === "custom" ? (
          <div
            key="custom"
            className={`ac-item${i === active ? " on" : ""}`}
            onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(entry);
            }}
          >
            <div className="an">{t("Custom Repair")}</div>
            <div className="aa">
              {t("Not in the catalogue — type it all in yourself")}
            </div>
          </div>
        ) : entry.kind === "text" ? (
          <div
            key="typed"
            className={`ac-item${i === active ? " on" : ""}`}
            onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(entry);
            }}
          >
            <div className="an">“{entry.value}”</div>
            <div className="aa">{t("Use what you typed — no product")}</div>
          </div>
        ) : (
          <Fragment key={entry.product.id}>
            {entry.kind === "close" && entries[i - 1]?.kind !== "close" && (
              <div className="ac-sec psel-sec">
                {t("Nothing matches all of “{query}” — close matches", {
                  query,
                })}
              </div>
            )}
            <div
              className={`ac-item${i === active ? " on" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(entry);
              }}
            >
              <div className="an">{entry.product.name}</div>
              <div className="aa">
                {[entry.product.category, entry.product.unit]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </Fragment>
        ),
      )}
    </div>,
    document.body,
  );
}

export function DeleteButton({
  table,
  id,
  onDone,
  onGuard,
  confirmMessage = t("Delete this row?"),
}: {
  table: string;
  id: string;
  onDone: () => Promise<void>;
  onGuard: (action: () => Promise<void>) => Promise<void>;
  /** What to ask first. A row that needs more than the ordinary question. */
  confirmMessage?: string;
}) {
  return (
    <button
      className="xbtn"
      onClick={() => {
        if (!window.confirm(confirmMessage)) return;
        void onGuard(async () => {
          await deleteRow(table, id);
          await onDone();
        });
      }}
    >
      ✕
    </button>
  );
}

const nextSort = (rows: readonly { sort_order?: number | null }[]): number =>
  rows.length ? Math.max(...rows.map((r) => r.sort_order ?? 0)) + 10 : 10;
